/**
 * Report Issue flow for Order Tracker V2 (loaded before tracker-v2.js).
 * Selector mode + multi-step drawer + Report Details; Save & Close localStorage; Finalise saveSolution API.
 */
(function (global) {
    const BUCKET = 'despatched';
    const STORAGE_KEY = 'lj_report_issue_drafts';
    const SPARE_LOOKUP_CACHE_KEY = 'lj_spare_parts_lookup';
    const SPARE_LOOKUP_TTL_MS = 30 * 60 * 1000;
    const DRAFT_VERSION = 'v1';
    const MAX_MEDIA = 10;
    const MAX_FILE_BYTES = 50 * 1024 * 1024;
    const ALLOWED_UPLOAD_TYPES = [
        { kind: 'image', ext: ['jpg', 'jpeg', 'jpe'], mime: ['image/jpeg'] },
        { kind: 'image', ext: ['png'], mime: ['image/png'] },
        { kind: 'image', ext: ['gif'], mime: ['image/gif'] },
        { kind: 'image', ext: ['webp'], mime: ['image/webp'] },
        { kind: 'image', ext: ['heic', 'heics'], mime: ['image/heic'] },
        { kind: 'image', ext: ['heif', 'heifs'], mime: ['image/heif'] },
        { kind: 'video', ext: ['mp4'], mime: ['video/mp4'] },
        { kind: 'video', ext: ['m4v'], mime: ['video/x-m4v'] },
        { kind: 'video', ext: ['mov', 'qt'], mime: ['video/quicktime'] },
        { kind: 'video', ext: ['webm'], mime: ['video/webm'] },
        { kind: 'video', ext: ['avi'], mime: ['video/x-msvideo'] },
        { kind: 'video', ext: ['3gp', '3gpp'], mime: ['video/3gpp'] },
    ];
    const DEFAULT_FINALISE_URL =
        'https://app-lj-shipping-charges.azurewebsites.net/ShopifyOrderStatus/upload-media';
    const SHOW_CHOOSE_SOLUTION = false;
    const VALIDATION_ERROR_ICON_SVG =
        '<svg class="order-amends__validation-error-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.2"/><path d="M8 4.5V8.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><circle cx="8" cy="11" r="0.75" fill="currentColor"/></svg>';
    const EDIT_ICON_SVG = `
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M4.99978 9H9M6.33319 1.94108L7.66659 3.35286M6.94477 1.29259C7.12171 1.10525 7.36169 1 7.61191 1C7.86214 1 8.10212 1.10525 8.27906 1.29259C8.456 1.47992 8.5554 1.73401 8.5554 1.99895C8.5554 2.26389 8.456 2.51797 8.27906 2.70531L2.94099 8.35763C2.83525 8.46958 2.70455 8.55148 2.56097 8.59575L1.28446 8.99011C1.24621 9.00192 1.20567 9.00263 1.16708 8.99216C1.12849 8.98169 1.09326 8.96043 1.06509 8.9306C1.03692 8.90078 1.01684 8.86348 1.00695 8.82262C0.997066 8.78176 0.997734 8.73883 1.00889 8.69834L1.38135 7.34679C1.42323 7.19495 1.50058 7.05673 1.60626 6.9449L6.94477 1.29259Z" stroke="#5C5F5C" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;

    const CHANGE_ICON_SVG = `
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M9 5C9 3.93913 8.57857 2.92172 7.82843 2.17157C7.07828 1.42143 6.06087 1 5 1C3.88176 1.00421 2.80843 1.44054 2.00444 2.21778L1 3.22222M3.22222 3.22222H1V1M1 5C1 6.06087 1.42143 7.07828 2.17157 7.82843C2.92172 8.57857 3.93913 9 5 9C6.11824 8.99579 7.19157 8.55946 7.99556 7.78222L9 6.77778M9 9V6.77778H6.77778" stroke="#5C5F5C" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;

    const CLOCK_ICON_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.25"/><path d="M8 5V8.25L10.25 9.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const STEPS = ['issue', 'quantity', 'uploads', 'comments'];
    const STEP_PROGRESS = { issue: 25, quantity: 50, uploads: 75, comments: 100 };

    let editModeActive = false;
    let handlersBound = false;
    let finaliseInFlight = false;
    let uploadInFlight = false;
    /** @type {Record<number, string>} */
    let validationErrorsByIndex = {};
    let panelErrorMessage = '';

    const itemRegistry = new Map();
    const shipmentRegistry = new Map();

    /** @type {object|null} */
    let draft = null;
    /** @type {object|null} in-progress issue while drawer open */
    let pendingIssue = null;
    /** @type {string|null} furthest drawer step opened for the pending issue */
    let pendingFurthestStep = null;
    /** True only when the drawer was opened from the reported-list Edit button. */
    let pendingIssueReopened = false;
    let drawerStep = null;
    let editingIssueIndex = null;
    let view = 'selector'; // selector | details | success | resend | contact | spares | spares-summary | submit-address
    let drawerContext = null; // 'issue' | 'solution' | 'fixings'
    let solutionIssueIndex = null;
    let pendingSolutionType = null;
    let resendIssueIndex = null;
    /** @type {object|null} solution payload (type/typeID/tag/name) chosen in the Solutions drawer, not yet committed to draft.issues until continueResendSelection() */
    let resendPendingSolution = null;
    let contactSupportIssueIndex = null;
    let sparePartsIssueIndex = null;
    /** @type {{ disposition: string|null, products: object[], quantities: Record<string, number>, loading: boolean, error: string, fieldError: string }} */
    let resendState = createEmptyResendState();
    /** @type {{ components: object[], fixings: object[], activeTab: string, componentsLoading: boolean, fixingsLoading: boolean, componentsError: string, fixingsError: string, componentsLoaded: boolean, fixingsLoaded: boolean }} */
    let sparePartsState = createEmptySparePartsState();

    function createEmptyResendState() {
        return {
            disposition: null,
            products: [],
            quantities: {},
            loading: false,
            error: '',
            fieldError: '',
            noneAvailable: false,
        };
    }

    function createEmptySparePartsState() {
        return {
            components: [],
            fixings: [],
            activeTab: 'components',
            componentsLoading: false,
            fixingsLoading: false,
            componentsError: '',
            fixingsError: '',
            componentsLoaded: false,
            fixingsLoaded: false,
            componentQty: {},
            selectedFixings: {},
            drawerFixingQty: {},
            fixingSnapshots: {},
            fixingsSearch: '',
        };
    }

    function t() {
        return global.orderTrackerTranslations || {};
    }

    function getRoot() {
        return document.getElementById('order-tracking');
    }

    function getOrderContext() {
        return global.__orderTrackerCachedOrderData || null;
    }

    function getOrderNumber() {
        const n = getOrderContext()?.orderSummary?.orderNo;
        return n != null ? String(n) : '';
    }

    function getReportIssueSupportUrl() {
        const root = getRoot();
        const returnsUrl = (root?.dataset.returnsSupportUrl || '').trim();
        const cancellationUrl = (root?.dataset.cancellationSupportUrl || '').trim();
        return returnsUrl || cancellationUrl;
    }

    function getOrderTrackerDrawer() {
        return document.querySelector('order-tracker-drawer');
    }

    function getStepEl() {
        return document.getElementById('order-report-issue-step');
    }

    function getShipmentsContainer() {
        return document.getElementById('order_items_container');
    }

    function getCustomerEmail() {
        return String(getOrderContext()?.customerInformation?.email || '');
    }

    function escapeHtml(value) {
        if (value == null) return '';
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function escapeHtmlAttr(value) {
        return escapeHtml(value);
    }

    function setVisiblyHidden(el, hidden) {
        if (!el) return;
        el.classList.toggle('hidden', hidden);
        if (hidden) el.setAttribute('hidden', '');
        else el.removeAttribute('hidden');
    }

    function nowIso() {
        return new Date().toISOString().replace(/\.\d{3}Z$/, '').replace(/Z$/, '');
    }

    function makeLineKey(batchIndex, itemIndex) {
        return `${BUCKET}:${batchIndex}:${itemIndex}`;
    }

    function issueOptions() {
        return Array.isArray(t().reportIssueOptions) ? t().reportIssueOptions : [];
    }

    function labelForIssueType(issueType) {
        const opt = issueOptions().find((o) => o.id === issueType);
        return opt?.label || issueType || '';
    }

    function decodeHtmlEntities(value) {
        const doc = new DOMParser().parseFromString(String(value || ''), 'text/html');
        return doc.documentElement.textContent || '';
    }

    function reportedIssuePayload(issueType) {
        const opt = issueOptions().find((o) => o.id === issueType);
        if (!opt || opt.typeID == null) return null;
        return {
            id: opt.typeID,
            title: decodeHtmlEntities(opt.label),
        };
    }

    function uploadsOptionalForIssueType(issueType) {
        return issueType === 'didnt_receive_product' || issueType === 'missing_parts';
    }

    function uploadTitleForIssueType(issueType) {
        const tr = t();
        if (issueType === 'quality_issue') {
            return (
                tr.reportIssueUploadTitleQualityIssue ||
                'Please provide any images / videos of the issues you have with the product'
            );
        }
        if (uploadsOptionalForIssueType(issueType)) {
            return tr.reportIssueUploadTitleOptional || 'Upload Image(s) (optional)';
        }
        return tr.reportIssueUploadTitle || '';
    }

    function commentsLabelForIssueType(issueType) {
        const tr = t();
        if (issueType === 'quality_issue') {
            return tr.reportIssueCommentsLabelQualityIssue || 'Please describe the issue';
        }
        return tr.reportIssueCommentsLabel || 'Additional Comments';
    }

    function uploadsMissingForPendingIssue() {
        if (!pendingIssue) return false;
        if (uploadsOptionalForIssueType(pendingIssue.issueType)) return false;
        return !(pendingIssue.media || []).length;
    }

    function savedIssueIsComplete(issue) {
        if (!issue?.issueType) return false;
        const qty = parseInt(issue.reportedQty, 10);
        if (!Number.isFinite(qty) || qty < 1) return false;
        if (uploadsOptionalForIssueType(issue.issueType)) return true;
        return (issue.media || []).length > 0;
    }

    // Upload instructions for damaged / wrong product — '' for other issues.
    function uploadInstructionsHtml(issueType) {
        const tr = t();
        const steps = (tr.reportIssueUploadInstructions?.[issueType] || []).filter(Boolean);
        if (!steps.length) return '';
        return `
                <div class="order-report-issue__upload-instructions">
                    <p class="order-report-issue__upload-instructions-intro">${escapeHtml(
                        tr.reportIssueUploadInstructionsIntro || ''
                    )}</p>
                    <ol class="order-report-issue__upload-instructions-list">${steps
                        .map((step) => `<li>${escapeHtml(step)}</li>`)
                        .join('')}</ol>
                </div>`;
    }

    function dispositionHiddenForIssueType(issueType) {
        return issueType === 'didnt_receive_product' || issueType === 'missing_parts';
    }

    function solutionOptions() {
        return Array.isArray(t().reportIssueSolutionOptions) ? t().reportIssueSolutionOptions : [];
    }

    function labelForSolutionType(solutionType) {
        const opt = solutionOptions().find((o) => o.id === solutionType);
        return opt?.label || solutionType || '';
    }

    function solutionOptionById(solutionType) {
        return solutionOptions().find((o) => o.id === solutionType) || null;
    }

    function solutionTypeId(solutionType) {
        return solutionOptionById(solutionType)?.typeID ?? null;
    }

    function solutionTypePayload(solutionType) {
        const opt = solutionOptionById(solutionType);
        if (!opt) return null;
        return {
            id: String(opt.typeID),
            tag: opt.tag || '',
            name: opt.name || opt.label || '',
        };
    }

    function defaultSolutionForIssueType(issue) {
        if (issue.issueType === 'didnt_receive_product' && issue.preferredSolution === 'replacement') {
            return directReplacementSolution(issue);
        }
        const payload = solutionTypePayload('contact_support');
        return {
            type: 'contact_support',
            typeID: solutionTypeId('contact_support'),
            tag: payload?.tag || '',
            name: payload?.name || '',
        };
    }

    function directReplacementSolution(issue) {
        const payload = solutionTypePayload('replacement');
        const estimate = replacementDeliveryEstimate(issue);
        return {
            type: 'replacement',
            typeID: solutionTypeId('replacement'),
            tag: payload?.tag || '',
            name: payload?.name || '',
            disposition: null,
            resendItems: [
                {
                    itemNumber: issue.itemID != null ? String(issue.itemID) : '',
                    quantity: parseInt(issue.reportedQty, 10) || 0,
                    rate: 0,
                    unitPrice: 0,
                    earliestDeliveryDate: estimate?.minDeliveryDateIso || null,
                    latestDeliveryDate: estimate?.maxDeliveryDateIso || null,
                },
            ],
        };
    }

    function parseNumber(value) {
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : 0;
    }

    function computeIssueLineGrossAmount(entry, quantity) {
        const maxQty = Math.max(0, parseInt(entry?.item?.quantity, 10) || 0);
        const lineGross = parseNumber(entry?.item?.grossAmount);
        if (maxQty <= 0 || quantity <= 0) return 0;
        return Math.round((lineGross / maxQty) * quantity * 100) / 100;
    }

    function getReportedQtyForLine(lineKey, excludeIssueIndex = null) {
        if (!draft?.issues?.length || !lineKey) return 0;
        return draft.issues.reduce((sum, issue, idx) => {
            if (issue.lineKey !== lineKey) return sum;
            if (excludeIssueIndex != null && idx === excludeIssueIndex) return sum;
            return sum + (parseInt(issue.reportedQty, 10) || 0);
        }, 0);
    }

    function getRemainingQtyForLine(lineKey, lineQty, excludeIssueIndex = null) {
        const reported = getReportedQtyForLine(lineKey, excludeIssueIndex);
        return Math.max(0, (parseInt(lineQty, 10) || 0) - reported);
    }

    function getMaxQtyForPendingIssue() {
        if (!pendingIssue?.lineKey) return 1;
        const exclude = editingIssueIndex != null ? editingIssueIndex : null;
        const remaining = getRemainingQtyForLine(
            pendingIssue.lineKey,
            pendingIssue.lineQty,
            exclude
        );
        return Math.max(1, remaining);
    }

    function syncSelectorLineUi() {
        if (!editModeActive) return;
        const tr = t();
        document.querySelectorAll('[data-oa-report-controls]').forEach((controls) => {
            const lineKey = controls.dataset.oaReportLineKey;
            const lineQty = parseInt(controls.dataset.oaReportLineQty, 10) || 0;
            const remaining = getRemainingQtyForLine(lineKey, lineQty);
            const container = controls.closest('.shipment__item-container');
            const remainingEl = container?.querySelector('[data-oa-report-remaining-qty]');
            const btn = controls.querySelector('.order-report-issue__report-btn');
            const exhausted = remaining <= 0;

            if (remainingEl) {
                remainingEl.textContent = replaceTokens(
                    tr.reportIssueRemainingQtyLine || 'Quantity: {{ qty }}',
                    { qty: remaining }
                );
            }

            if (btn) {
                btn.disabled = exhausted;
                btn.classList.toggle('order-report-issue__report-btn--exhausted', exhausted);
                btn.textContent = exhausted
                    ? tr.reportIssueAllReported 
                    : tr.reportIssueBtn || 'Report Issue';
            }
        });
    }

    function replaceTokens(template, vars) {
        let out = String(template || '');
        Object.keys(vars || {}).forEach((key) => {
            out = out.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(vars[key] ?? ''));
        });
        return out;
    }

    function generateId(prefix) {
        return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    }

    /* --- localStorage --- */

    function readAllDrafts() {
        try {
            const raw = global.localStorage.getItem(STORAGE_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.warn('Report Issue: failed to read drafts', e);
            return [];
        }
    }

    function writeAllDrafts(drafts) {
        try {
            global.localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
        } catch (e) {
            console.warn('Report Issue: failed to write drafts', e);
        }
    }

    function findDraftForOrder(order) {
        const matches = readAllDrafts().filter(
            (d) =>
                d &&
                d.version === DRAFT_VERSION &&
                d.status === 'in_progress' &&
                String(d.order) === String(order)
        );
        if (!matches.length) return null;
        matches.sort((a, b) => String(b.dateSaved || '').localeCompare(String(a.dateSaved || '')));
        return matches[0];
    }

    function upsertDraftInStorage(nextDraft) {
        if (!nextDraft?.order) return;
        const all = readAllDrafts().filter(
            (d) =>
                !(
                    d &&
                    d.version === DRAFT_VERSION &&
                    String(d.order) === String(nextDraft.order) &&
                    d.status === 'in_progress'
                )
        );
        all.push(nextDraft);
        writeAllDrafts(all);
    }

    function removeDraftForOrder(order) {
        if (!order) return;
        const all = readAllDrafts().filter(
            (d) =>
                !(
                    d &&
                    d.version === DRAFT_VERSION &&
                    String(d.order) === String(order) &&
                    d.status === 'in_progress'
                )
        );
        writeAllDrafts(all);
    }

    function createEmptyDraft(order) {
        const ts = nowIso();
        return {
            order: String(order),
            dateCreated: ts,
            dateSaved: ts,
            version: DRAFT_VERSION,
            status: 'in_progress',
            ui: { step: 'selector', activeIssueIndex: null },
            issues: [],
            shippingAddress: null,
        };
    }

    function flushPersist() {
        if (!draft?.order || !(draft.issues?.length > 0)) return;
        draft.dateSaved = nowIso();
        draft.ui = {
            step: view === 'details' ? 'details' : 'selector',
            activeIssueIndex: editingIssueIndex,
        };
        upsertDraftInStorage(JSON.parse(JSON.stringify(draft)));
    }

    function normalizeIssuesForCompare(issues) {
        return (issues || []).map((issue) => ({
            _localId: issue._localId || null,
            lineKey: issue.lineKey || '',
            issueType: issue.issueType || null,
            reportedQty: parseInt(issue.reportedQty, 10) || 0,
            description: issue.description || '',
            media: (issue.media || []).map((m) => ({
                id: m.id ?? null,
                url: m.url || '',
                fileName: m.fileName || '',
            })),
            solution: issue.solution
                ? {
                      type: issue.solution.type || null,
                      typeID: issue.solution.typeID ?? null,
                  }
                : null,
        }));
    }

    function getPersistedDraftSnapshot() {
        const order = draft?.order || getOrderNumber();
        if (!order) return '[]';
        const stored = findDraftForOrder(order);
        return JSON.stringify(normalizeIssuesForCompare(stored?.issues));
    }

    function getWorkingDraftSnapshot() {
        return JSON.stringify(normalizeIssuesForCompare(draft?.issues));
    }

    function hasUnsavedChanges() {
        if (!editModeActive || !draft) return false;
        if (
            pendingIssue &&
            (pendingIssue.issueType || (pendingIssue.media || []).length > 0 || pendingIssue.description)
        ) {
            return true;
        }
        return getWorkingDraftSnapshot() !== getPersistedDraftSnapshot();
    }

    function clearValidationErrors() {
        validationErrorsByIndex = {};
        panelErrorMessage = '';
    }

    function validationErrorBannerHtml(message) {
        if (!message) return '';
        return `<p class="order-amends__validation-error-message order-report-issue__card-error" role="alert">
            ${VALIDATION_ERROR_ICON_SVG}
            <span data-oa-validation-error-text>${escapeHtml(message)}</span>
        </p>`;
    }

    /* --- Registry / eligibility (despatched placeholder) --- */

    function isShipmentEligibleForReportIssue(shipmentOrBatch) {
        return Boolean((shipmentOrBatch?.items || []).length);
    }

    function isLineEligibleForReportIssue(item) {
        const ordered = parseInt(item?.quantity, 10) || 0;
        if (ordered <= 0) return false;
        const returned = window.OrderReturnsFlow?.getReturnedQuantity?.(item) ?? 0;
        return returned < ordered;
    }

    function rebuildReportIssueRegistry(orderData) {
        itemRegistry.clear();
        shipmentRegistry.clear();

        const shipments = orderData?.orderDetails?.orderItems?.shipments || [];
        shipments.forEach((shipmentOrBatch, batchIndex) => {
            if (!isShipmentEligibleForReportIssue(shipmentOrBatch)) return;

            const eligibleItems = (shipmentOrBatch.items || [])
                .map((item, itemIndex) => ({ item, itemIndex }))
                .filter(({ item }) => isLineEligibleForReportIssue(item));
            
            if (!eligibleItems.length) return;

            shipmentRegistry.set(batchIndex, { shipmentOrBatch });
            eligibleItems.forEach(({ item, itemIndex }) => {
                itemRegistry.set(makeLineKey(batchIndex, itemIndex), {
                    item,
                    batchIndex,
                    itemIndex,
                    shipmentOrBatch,
                });
            });
        });
    }

    function updateReportIssueCtaVisibility() {
        const btn = document.getElementById('order-report-issue-start');
        if (!btn) return;
        btn.classList.toggle('hidden', itemRegistry.size === 0 || editModeActive);
    }

    /* --- Mode / views --- */

    function getSelectorBanner() {
        return document.getElementById('order-report-issue-selector-banner');
    }

    function syncBannerCopy() {
        const tr = t();
        const banner = getSelectorBanner();
        if (!banner) return;
        const titleEl = banner.querySelector('.order-report-issue__selector-banner-title');
        const introEl = banner.querySelector('.order-report-issue__selector-banner-intro');
        let title = tr.reportIssueDetailsTitle;
        let intro = tr.reportIssueDetailsIntro;
        if (view === 'details') {
            title = tr.reportIssueReportedTitle;
            intro = tr.reportIssueReportedIntro;
        } else if (view === 'contact') {
            title = tr.reportIssueContactSupportTitle;
            intro = tr.reportIssueContactSupportIntro;
        } else if (view === 'resend') {
            title = tr.reportIssueResendTitle;
            intro = tr.reportIssueResendIntro;
        } else if (view === 'submit-address') {
            title = tr.reportIssueReportedTitle;
            intro = tr.reportIssueSubmitAddressIntro;
        } else if (view === 'spares' || view === 'spares-summary') {
            const issue = draft?.issues?.[sparePartsIssueIndex];
            if (view === 'spares-summary') {
                title = tr.reportIssueSparePartsSummaryTitle;
                intro = tr.reportIssueSparePartsSummaryIntro;
            } else {
                title = (issue?.productName && String(issue.productName).trim()) ||
                    tr.reportIssueSparePartsTitle;
                intro = tr.reportIssueSparePartsIntro;
            }
        }
        if (titleEl) titleEl.textContent = title;
        if (introEl) introEl.textContent = intro.replace(/&#39;/g, "'");
    }

    function getBannerActionsEl() {
        return getSelectorBanner()?.querySelector('.order-report-issue-actions__edit-state');
    }

    function clearJsBannerButtons(container) {
        container?.querySelectorAll('[data-oa-report-banner-js]').forEach((el) => el.remove());
    }

    function appendBannerButton(container, options) {
        const { id, action, label, submit, disabled } = options;
        console.log(label);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = submit
            ? 'button button--primary button--action-submit'
            : 'button button--cancel-changes';
        if (id) btn.id = id;
        btn.dataset.oaReportAction = action;
        btn.dataset.oaReportBannerJs = 'true';
        btn.innerHTML = label;
        btn.disabled = Boolean(disabled);
        container.appendChild(btn);
        return btn;
    }

    function updateHeaderActions() {
        const issueCount = draft?.issues?.length || 0;
        const closeBtn = document.getElementById('order-report-issue-close');
        const reportedBtn = document.getElementById('order-report-issue-reported');
        const actionsEl = getBannerActionsEl();
        const tr = t();
        if (!editModeActive) return;

        const showBanner =
            view === 'selector' ||
            view === 'details' ||
            view === 'contact' ||
            view === 'resend' ||
            view === 'spares' ||
            view === 'spares-summary' ||
            view === 'submit-address';
        setVisiblyHidden(getSelectorBanner(), !showBanner);
        if (showBanner) syncBannerCopy();

        const isSelector = view === 'selector';
        setVisiblyHidden(closeBtn, !isSelector);
        setVisiblyHidden(reportedBtn, !isSelector);
        if (reportedBtn) reportedBtn.disabled = issueCount === 0;

        if (!actionsEl) return;
        clearJsBannerButtons(actionsEl);

        if (view === 'details') {
            appendBannerButton(actionsEl, {
                id: 'order-report-issue-discard',
                action: 'discard',
                label: tr.reportIssueDiscard,
            });
            appendBannerButton(actionsEl, {
                id: 'order-report-issue-finalise',
                action: 'finalise',
                label: finaliseInFlight
                    ? tr.reportIssueFinaliseSubmitting || 'Submitting…'
                    : tr.reportIssueSubmitRequest || 'Submit Request',
                submit: true,
                disabled: finaliseInFlight || issueCount === 0,
            });
        } else if (view === 'contact') {
            appendBannerButton(actionsEl, {
                action: 'contact-back',
                label: tr.reportIssueContactSupportBack || 'Back',
            });
            appendBannerButton(actionsEl, {
                action: 'contact-select-solution',
                label: tr.reportIssueContactSupportSelect || 'Select Solution',
                submit: true,
            });
        } else if (view === 'resend') {
            appendBannerButton(actionsEl, {
                action: 'resend-back',
                label: tr.reportIssueResendBack || 'Back',
            });
            appendBannerButton(actionsEl, {
                action: 'resend-continue',
                label: tr.reportIssueResendContinue || 'Continue',
                submit: true,
                disabled: resendState.loading,
            });
        } else if (view === 'spares') {
            appendBannerButton(actionsEl, {
                action: 'spares-back',
                label: tr.reportIssueSparePartsBack || 'Back',
            });
            appendBannerButton(actionsEl, {
                action: 'spares-continue',
                label: tr.reportIssueResendContinue || 'Continue',
                submit: true,
                disabled: selectedSpareCount() === 0,
            });
        } else if (view === 'submit-address') {
            appendBannerButton(actionsEl, {
                action: 'submit-address-back',
                label: tr.reportIssueBack || 'Back',
            });
            appendBannerButton(actionsEl, {
                id: 'order-report-issue-finalise',
                action: 'finalise',
                label: finaliseInFlight
                    ? tr.reportIssueFinaliseSubmitting || 'Submitting…'
                    : tr.reportIssueSubmitReport || 'Submit Report',
                submit: true,
                disabled: finaliseInFlight || issueCount === 0,
            });
        } else if (view === 'spares-summary') {
            appendBannerButton(actionsEl, {
                action: 'spares-summary-back',
                label: tr.reportIssueSparePartsBack || 'Back',
            });
            appendBannerButton(actionsEl, {
                action: 'spares-summary-select',
                label: tr.reportIssueContactSupportSelect || 'Select Solution',
                submit: true,
            });
        }
    }

    function showSelectorView() {
        hideReportIssueAddressEmbed();
        view = 'selector';
        setVisiblyHidden(getStepEl(), true);
        setVisiblyHidden(getShipmentsContainer(), false);
        if (draft) draft.ui = { ...(draft.ui || {}), step: 'selector' };
        updateHeaderActions();
        syncSelectorLineUi();
    }

    function showDetailsView() {
        hideReportIssueAddressEmbed();
        view = 'details';
        renderReportDetailsPanel();
        setVisiblyHidden(getShipmentsContainer(), true);
        setVisiblyHidden(getStepEl(), false);
        if (draft) draft.ui = { ...(draft.ui || {}), step: 'details' };
        updateHeaderActions();
    }

    function setReportIssueEditMode(active) {
        editModeActive = active;
        const root = getRoot();
        if (!root) return;

        root.classList.toggle('order-report-issue--active-state', active);
        root.classList.toggle('order-report-issue--read-state', !active);

        updateReportIssueCtaVisibility();
        updateHeaderActions();

        if (active) {
            syncSelectorLineUi();
        }

        if (!active) {
            setVisiblyHidden(getStepEl(), true);
            setVisiblyHidden(getShipmentsContainer(), false);
            setVisiblyHidden(document.querySelector('.order-header'), false);
            setVisiblyHidden(getSelectorBanner(), true);
            view = 'selector';
        }
    }

    function exitConflictingFlows() {
        if (global.OrderReturnsFlow?.isReturnsFlowActive?.()) {
            global.OrderReturnsFlow.exitReturnsMode?.();
        }
        if (global.OrderAmendsFlow?.isOrderCancelFlowActive?.()) {
            getOrderTrackerDrawer()?.close();
        }
        document.querySelector('.order-layout')?.classList.remove('change-address-active');
        const addressPanel = document.getElementById('address-update-panel');
        if (addressPanel) addressPanel.classList.add('hidden');
    }

    function ensureDraft() {
        const order = getOrderNumber();
        if (!order) return null;
        if (draft && String(draft.order) === order) return draft;

        const stored = findDraftForOrder(order);
        draft = stored ? JSON.parse(JSON.stringify(stored)) : createEmptyDraft(order);
        return draft;
    }

    function beginReportIssueFlow() {
        if (global.OrderAmendsFlow?.isOrderCancelFlowActive?.()) return;
        if (itemRegistry.size === 0) return;

        exitConflictingFlows();
        ensureDraft();
        pendingIssue = null;
        pendingFurthestStep = null;
        pendingIssueReopened = false;
        drawerStep = null;
        editingIssueIndex = null;
        clearValidationErrors();
        setReportIssueEditMode(true);

        if (draft?.issues?.length > 0 && draft.ui?.step === 'details') {
            showDetailsView();
        } else if (draft?.issues?.length > 0) {
            showDetailsView();
        } else {
            showSelectorView();
        }
        syncSelectorLineUi();
    }

    function exitReportIssueMode(options = {}) {
        const { clearDraft = false, skipUnsavedConfirm = false } = options;
        const tr = t();

        if (
            !skipUnsavedConfirm &&
            !clearDraft &&
            hasUnsavedChanges() &&
            !global.confirm(
                tr.reportIssueLeaveUnsavedConfirm || 'You have unsaved changes. Leave without saving?'
            )
        ) {
            return false;
        }

        hideReportIssueAddressEmbed();

        const drawer = getOrderTrackerDrawer();
        if (drawer) {
            drawer.setCloseBlocked?.(false);
            drawer.close();
        }

        if (clearDraft && draft?.order) {
            removeDraftForOrder(draft.order);
        }

        draft = null;
        pendingIssue = null;
        pendingFurthestStep = null;
        pendingIssueReopened = false;
        drawerStep = null;
        drawerContext = null;
        solutionIssueIndex = null;
        pendingSolutionType = null;
        editingIssueIndex = null;
        resendIssueIndex = null;
        resendPendingSolution = null;
        contactSupportIssueIndex = null;
        sparePartsIssueIndex = null;
        resendState = createEmptyResendState();
        sparePartsState = createEmptySparePartsState();
        finaliseInFlight = false;
        uploadInFlight = false;
        clearValidationErrors();
        setReportIssueEditMode(false);
        return true;
    }

    function saveAndCloseReportIssue() {
        flushPersist();
        exitReportIssueMode({ clearDraft: false, skipUnsavedConfirm: true });
    }

    function handleBeforeUnload(ev) {
        if (!editModeActive || !hasUnsavedChanges()) return;
        ev.preventDefault();
        ev.returnValue = '';
    }

    /* --- Pending issue builders --- */

    function buildPendingFromLine(lineKey) {
        const entry = itemRegistry.get(lineKey);
        if (!entry) return null;
        const item = entry.item;
        const lineQty = Math.max(1, parseInt(item.quantity, 10) || 1);
        const remaining = getRemainingQtyForLine(lineKey, lineQty);
        if (remaining <= 0) return null;
        return {
            _localId: generateId('issue'),
            originalSOLineID: item.id != null ? item.id : null,
            itemID: item.itemNumber != null ? item.itemNumber : item.id != null ? item.id : null,
            reportingCode: item.reportingCode != null ? String(item.reportingCode) : '',
            lineKey,
            productName: String(item.itemDescription || ''),
            imageUrl: item.itemImageUrl || '',
            lineQty,
            issueType: null,
            reportedQty: Math.min(1, remaining),
            description: '',
            media: [],
            solution: null,
        };
    }

    function cloneIssueForEdit(issue, index) {
        editingIssueIndex = index;
        return {
            ...JSON.parse(JSON.stringify(issue)),
            _localId: issue._localId || generateId('issue'),
            lineQty: issue.lineQty || 1,
            imageUrl: issue.imageUrl || '',
        };
    }

    /* --- Drawer rendering --- */
    function furthestStepIndex(steps) {
        const idx = steps.indexOf(pendingFurthestStep);
        if (idx >= 0) return idx;
        if (pendingFurthestStep) return steps.length - 1;
        return -1;
    }

    function markFurthestStep(step) {
        const steps = stepsForPendingIssue();
        const next = steps.indexOf(step);
        if (next < 0) return;
        if (next > furthestStepIndex(steps)) pendingFurthestStep = step;
    }

    function summaryStepVisible(stepName) {
        if (!pendingIssue || drawerStep === stepName) return false;
        const steps = stepsForPendingIssue();
        const target = steps.indexOf(stepName);
        if (target < 0) return false;
        if (drawerStep === 'review') return true;
        const current = steps.indexOf(drawerStep);
        if (current < 0) return false;
        return target < current;
    }

    function stepsForPendingIssue() {
        if (usesReplacementCheck(pendingIssue) && pendingIssue.replacementCheck?.status !== 'none') {
            return [...STEPS, 'solution'];
        }
        return STEPS;
    }

    function progressPercent() {
        if (drawerStep === 'review') return 100;
        const steps = stepsForPendingIssue();
        const idx = steps.indexOf(drawerStep);
        if (steps !== STEPS && idx >= 0) return Math.round(((idx + 1) / steps.length) * 100);
        return STEP_PROGRESS[drawerStep] || 25;
    }

    function preferredSolutionSummaryText() {
        const tr = t();
        if (pendingIssue?.preferredSolution === 'contact_support') {
            return tr.reportIssuePreferredNo || 'No, contact support';
        }
        if (pendingIssue?.preferredSolution !== 'replacement') return '';
        const yes = tr.reportIssuePreferredYes || "Yes, I'd like a replacement.";
        const range = replacementDeliveryRange(pendingIssue);
        if (!range) return yes;
        const delivery = replaceTokens(tr.reportIssuePreferredDelivery || 'Get it by {{ date }}', {
            date: range,
        });
        return `${yes} ${delivery}`;
    }

    function renderSummaryStackHtml() {
        const tr = t();
        if (!pendingIssue?.issueType) return '';

        const rows = [];
        if (summaryStepVisible('issue')) {
            const reasonLabel = labelForIssueType(pendingIssue.issueType);
            rows.push(`
            <div class="order-report-issue__summary-row">
                <span class="order-report-issue__summary-text">${
                    replaceTokens(tr.reportIssueReasonSummary || 'Reason: {{ reason }}', {
                        reason: reasonLabel,
                    }
                )}</span>
                <button type="button" class="button order-report-issue__summary-edit" data-oa-report-action="edit-step" data-oa-report-step="issue">${escapeHtml(
                    tr.reportIssueEdit || 'Edit'
                )}</button>
            </div>`);
        }

        if (summaryStepVisible('quantity') && pendingIssue.reportedQty != null) {
            rows.push(`
            <div class="order-report-issue__summary-row">
                <span class="order-report-issue__summary-text">${escapeHtml(
                    replaceTokens(tr.reportIssueQuantitySummary || 'Quantity: {{ qty }}', {
                        qty: pendingIssue.reportedQty,
                    })
                )}</span>
                <button type="button" class="button order-report-issue__summary-edit" data-oa-report-action="edit-step" data-oa-report-step="quantity">${escapeHtml(
                    tr.reportIssueEdit
                )}</button>
            </div>`);
        }

        if (summaryStepVisible('uploads')) {
            const media = pendingIssue.media || [];
            const thumbs = media
                .map(
                    (m, i) => `
                <div class="order-report-issue__thumb" data-media-index="${i}">
                    ${
                        m.type === 'video'
                            ? `<span class="order-report-issue__thumb-video">▶</span>`
                            : `<img src="${escapeHtmlAttr(mediaThumbSrc(m))}" alt="">`
                    }
                    <button type="button" class="order-report-issue__thumb-remove" data-oa-report-action="remove-media" data-media-index="${i}" aria-label="Remove">&times;</button>
                </div>`
                )
                .join('');
            rows.push(`
            <div class="order-report-issue__summary-row order-report-issue__summary-row--uploads">
                <div class="order-report-issue__summary-uploads">
                    <p class="order-report-issue__summary-uploads-title${
                        thumbs ? '' : ' order-report-issue__summary-uploads-title--solo'
                    }">${escapeHtml(tr.reportIssueUploadsSummary || 'Uploads')}</p>
                    ${thumbs ? `<div class="order-report-issue__thumbs">${thumbs}</div>` : ''}
                </div>
                <button type="button" class="button order-report-issue__summary-edit" data-oa-report-action="edit-step" data-oa-report-step="uploads">${escapeHtml(
                    tr.reportIssueEdit || 'Edit'
                )}</button>
            </div>`);
        }

        const comments = String(pendingIssue.description || '').trim();
        if (summaryStepVisible('comments')) {
            rows.push(`
            <div class="order-report-issue__summary-row order-report-issue__summary-row--comments">
                <div class="order-report-issue__summary-comments">
                    <p class="order-report-issue__summary-uploads-title${
                        comments ? '' : ' order-report-issue__summary-uploads-title--solo'
                    }">${escapeHtml(commentsLabelForIssueType(pendingIssue.issueType))}</p>
                    ${
                        comments
                            ? `<p class="order-report-issue__summary-comments-text">${escapeHtml(comments)}</p>`
                            : ''
                    }
                </div>
                <button type="button" class="button order-report-issue__summary-edit" data-oa-report-action="edit-step" data-oa-report-step="comments">${escapeHtml(
                    tr.reportIssueEdit || 'Edit'
                )}</button>
            </div>`);
        }

        if (!rows.length) return '';
        return `<div class="order-report-issue__summary-stack">${rows.join('')}</div>`;
    }

    function renderPreferredSolutionHtml() {
        const tr = t();
        const choice = pendingIssue.preferredSolution;
        const questionHtml = `
                <p class="order-report-issue__preferred-question">${
                    tr.reportIssuePreferredQuestion || 'Would you like a replacement for affected products at no cost?'
                }</p>`;
        if (pendingIssue.replacementCheck?.status === 'loading') {
            return `${questionHtml}
                <p class="order-report-issue__hint" aria-live="polite">${
                    tr.reportIssuePreferredChecking || 'Checking availability…'
                }</p>
                <p class="order-report-issue__field-error" data-oa-report-error hidden></p>`;
        }
        const replacementBlocked = preferredReplacementExceedsQuantity(pendingIssue);
        const deliveryRange = replacementDeliveryRange(pendingIssue);
        const deliveryHtml = deliveryRange
            ? ` <span class="order-report-issue__preferred-delivery">${replaceTokens(
                  tr.reportIssuePreferredDelivery || 'Get it by {{ date }}',
                  { date: escapeHtml(deliveryRange) }
              )}</span>`
            : '';
        return `${questionHtml}
                <div class="order-report-issue__preferred-options" role="radiogroup">
                    <label class="order-report-issue__preferred">
                        <input type="radio" class="order-report-issue__preferred-input" name="report-preferred-solution" value="replacement"${
                            !replacementBlocked && choice === 'replacement' ? ' checked' : ''
                        }${replacementBlocked ? ' disabled' : ''}>
                        <span class="order-report-issue__preferred-text">${
                            tr.reportIssuePreferredYes || 'Yes, I&#39;d like a replacement.'
                        }${deliveryHtml}</span>
                    </label>
                    <label class="order-report-issue__preferred">
                        <input type="radio" class="order-report-issue__preferred-input" name="report-preferred-solution" value="contact_support"${
                            choice === 'contact_support' ? ' checked' : ''
                        }>
                        <span class="order-report-issue__preferred-text">${
                            tr.reportIssuePreferredNo || 'No, contact support'
                        }</span>
                    </label>
                </div>
                <p class="order-report-issue__replacement-blocked" data-oa-report-replacement-blocked role="status" hidden></p>
                <p class="order-report-issue__field-error" data-oa-report-error hidden></p>`;
    }

    function reviewShowsPreferredSolution() {
        return (
            pendingIssue?.issueType === 'didnt_receive_product' &&
            pendingIssue.replacementCheck?.status !== 'none'
        );
    }

    function renderStepBodyHtml() {
        const tr = t();
        if (!pendingIssue) return '';
        if (drawerStep === 'review') {
            if (reviewShowsPreferredSolution()) return renderPreferredSolutionHtml();
            return '<p class="order-report-issue__field-error" data-oa-report-error hidden></p>';
        }

        if (drawerStep === 'issue') {
            const options = issueOptions()
                .map((opt) => {
                    const checked = pendingIssue.issueType === opt.id ? ' checked' : '';
                    return `
                    <label class="order-report-issue__option">
                        <input type="radio" name="report-issue-type" value="${escapeHtmlAttr(opt.id)}"${checked}>
                        <span>${opt.label}</span>
                    </label>`;
                })
                .join('');
            return `
                <p class="order-report-issue__intro">${escapeHtml(tr.reportIssueSelectIntro || '')}</p>
                <div class="order-report-issue__options" role="radiogroup">${options}</div>
                <p class="order-report-issue__field-error" data-oa-report-error hidden></p>`;
        }

        if (drawerStep === 'quantity') {
            const maxQty = getMaxQtyForPendingIssue();
            const qty = Math.max(1, Math.min(maxQty, parseInt(pendingIssue.reportedQty, 10) || 1));
            pendingIssue.reportedQty = qty;
            const minusDisabled = qty <= 1 ? ' disabled' : '';
            const plusDisabled = qty >= maxQty ? ' disabled' : '';
            return `
                <p class="order-report-issue__intro">${escapeHtml(tr.reportIssueQuantityLabel || '')}</p>
                <div class="order-report-issue__qty-stepper" role="group" aria-label="${escapeHtmlAttr(
                    tr.reportIssueQuantityLabel || 'Quantity'
                )}">
                    <button type="button" class="order-report-issue__qty-btn" data-oa-report-action="qty-dec"${minusDisabled} aria-label="Decrease">−</button>
                    <span class="order-report-issue__qty-value" data-oa-report-qty>${qty}</span>
                    <button type="button" class="order-report-issue__qty-btn" data-oa-report-action="qty-inc"${plusDisabled} aria-label="Increase">+</button>
                </div>
                <p class="order-report-issue__field-error" data-oa-report-error hidden></p>`;
        }

        if (drawerStep === 'uploads') {
            const thumbs = (pendingIssue.media || [])
                .map(
                    (m, i) => `
                <div class="order-report-issue__thumb" data-media-index="${i}">
                    ${
                        m.type === 'video'
                            ? `<span class="order-report-issue__thumb-video">▶</span>`
                            : `<img src="${escapeHtmlAttr(mediaThumbSrc(m))}" alt="">`
                    }
                    <button type="button" class="order-report-issue__thumb-remove" data-oa-report-action="remove-media" data-media-index="${i}" aria-label="Remove">&times;</button>
                </div>`
                )
                .join('');
            return `
                <p class="order-report-issue__upload-title">${escapeHtml(
                    uploadTitleForIssueType(pendingIssue.issueType)
                )}</p>
                ${uploadInstructionsHtml(pendingIssue.issueType)}
                <p class="order-report-issue__hint">${escapeHtml(tr.reportIssueUploadHint || '')}</p>
                <label class="order-report-issue__upload-area">
                    <input type="file" data-oa-report-file accept="${escapeHtmlAttr(uploadAcceptAttribute())}" multiple hidden>
                    <span class="order-report-issue__upload-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 16V4M12 4L7 9M12 4L17 9" stroke="#424442" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 16V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V16" stroke="#424442" stroke-width="1.5" stroke-linecap="round"/></svg>
                    </span>
                    <span>${escapeHtml(tr.reportIssueUploadBtn || 'Upload Image(s)')}</span>
                </label>
                <div class="order-report-issue__thumbs">${thumbs}</div>
                <p class="order-report-issue__field-error" data-oa-report-error hidden></p>`;
        }

        if (drawerStep === 'solution') return renderPreferredSolutionHtml();

        // comments
        return `
            <p class="order-report-issue__upload-title">${escapeHtml(commentsLabelForIssueType(pendingIssue.issueType))}</p>
            <textarea class="order-report-issue__comments" data-oa-report-comments rows="5" placeholder="${escapeHtmlAttr(
                tr.reportIssueCommentsPlaceholder || ''
            )}">${escapeHtml(pendingIssue.description || '')}</textarea>
            <p class="order-report-issue__field-error" data-oa-report-error hidden></p>`;
    }

    function footerButtonsHtml(primaryAction, primaryLabel, secondaryAction, secondaryLabel) {
        const secondary = secondaryAction
            ? `<button type="button" class="button button--secondary" data-oa-report-action="${escapeHtmlAttr(
                  secondaryAction
              )}">${escapeHtml(secondaryLabel)}</button>`
            : '';
        return `
            <div class="button-group order-report-issue__drawer-footer">
                <button type="button" class="button button--primary" data-oa-report-action="${escapeHtmlAttr(
                    primaryAction
                )}">${escapeHtml(primaryLabel)}</button>
                ${secondary}
            </div>`;
    }

    function openDrawerStep(step) {
        const tr = t();
        if (!pendingIssue) return;
        drawerContext = 'issue';
        drawerStep = step;

        if (['uploads', 'comments', 'solution', 'review'].includes(step)) ensureReplacementCheck();
        markFurthestStep(step);

        const steps = stepsForPendingIssue();
        const isReview = step === 'review';
        const secondary =
            step === 'issue'
                ? { action: 'drawer-close', label: tr.reportIssueClose || 'Close' }
                : { action: 'drawer-back', label: tr.reportIssueBack || 'Back' };
        const primary =
            isReview || step === steps[steps.length - 1]
                ? { action: 'drawer-finish', label: tr.reportIssueFinish || 'Finish' }
                : { action: 'drawer-next', label: tr.reportIssueNext || 'Next' };

        const rootHtml = `
            <div class="order-report-issue-drawer-root">
                <div class="order-report-issue__item-bar">
                    <p class="order-report-issue__item-name">${escapeHtml(pendingIssue.productName || '')}</p>
                    <div class="order-report-issue__progress" role="progressbar" aria-valuenow="${progressPercent()}" aria-valuemin="0" aria-valuemax="100">
                        <span class="order-report-issue__progress-fill" style="width:${progressPercent()}%"></span>
                    </div>
                </div>
                ${renderSummaryStackHtml()}
                <div class="order-report-issue__step-body">${renderStepBodyHtml()}</div>
            </div>`;

        const drawer = getOrderTrackerDrawer();
        if (!drawer) return;
        drawer.open({
            title: tr.reportIssueDrawerTitle || 'Reporting Issue',
            html: rootHtml,
            footerHtml: footerButtonsHtml(
                primary.action,
                primary.label,
                secondary?.action,
                secondary?.label
            ),
        });
        if (step === 'solution' || step === 'review') syncPreferredReplacementNotice();
    }

    function showDrawerError(message) {
        const el = getOrderTrackerDrawer()?.querySelector('[data-oa-report-error]');
        if (!el) return;
        el.textContent = message || '';
        setVisiblyHidden(el, !message);
    }

    function syncPendingFromDom() {
        const drawer = getOrderTrackerDrawer();
        if (!drawer || !pendingIssue) return;

        if (drawerStep === 'quantity') {
            const el = drawer.querySelector('[data-oa-report-qty]');
            const n = parseInt(el?.textContent ?? el?.value, 10);
            if (Number.isFinite(n)) pendingIssue.reportedQty = n;
        }
        if (drawerStep === 'comments') {
            const ta = drawer.querySelector('[data-oa-report-comments]');
            if (ta) pendingIssue.description = ta.value || '';
        }
        if (drawerStep === 'solution') {
            const picked = drawer.querySelector('input[name="report-preferred-solution"]:checked');
            if (picked) pendingIssue.preferredSolution = picked.value;
        }
    }

    function validateCurrentStep() {
        const tr = t();
        syncPendingFromDom();
        showDrawerError('');

        if (drawerStep === 'issue') {
            if (!pendingIssue?.issueType) {
                showDrawerError(tr.reportIssueSelectRequired || 'Please select an issue.');
                return false;
            }
            return true;
        }
        if (drawerStep === 'quantity') {
            const qty = pendingIssue.reportedQty;
            const max = getMaxQtyForPendingIssue();
            if (!Number.isFinite(qty) || qty < 1 || qty > max) {
                showDrawerError(tr.reportIssueQuantityRequired || 'Enter a valid quantity.');
                return false;
            }
            pendingIssue.reportedQty = Math.max(1, Math.min(max, qty));
            return true;
        }
        if (drawerStep === 'solution') {
            if (pendingIssue?.replacementCheck?.status === 'loading') return false;
            if (syncPreferredReplacementNotice()) return false;
            if (!pendingIssue?.preferredSolution) {
                showDrawerError(tr.reportIssuePreferredRequired || 'Please select an option.');
                return false;
            }
            return true;
        }
        if (drawerStep === 'uploads') {
            if (uploadsMissingForPendingIssue()) {
                showDrawerError(
                    tr.reportIssueFinaliseUploadsRequired || 'At least one upload is required.'
                );
                return false;
            }
            return true;
        }
        if (drawerStep === 'review' && syncPreferredReplacementNotice()) return false;
        return true;
    }

    function stepReportedQty(delta) {
        if (!pendingIssue || drawerStep !== 'quantity') return;
        const max = getMaxQtyForPendingIssue();
        const current = parseInt(pendingIssue.reportedQty, 10) || 1;
        pendingIssue.reportedQty = Math.max(1, Math.min(max, current + delta));
        openDrawerStep('quantity');
    }

    function goNextStep() {
        if (!validateCurrentStep()) return;
        const steps = stepsForPendingIssue();
        const idx = steps.indexOf(drawerStep);
        if (idx < 0 || idx >= steps.length - 1) return;
        openDrawerStep(steps[idx + 1]);
    }

    function goBackStep() {
        syncPendingFromDom();
        const steps = stepsForPendingIssue();
        if (drawerStep === 'review') {
            const prev = steps[steps.length - 2];
            if (prev) openDrawerStep(prev);
            return;
        }
        const idx = steps.indexOf(drawerStep);
        if (idx <= 0) return;
        openDrawerStep(steps[idx - 1]);
    }

    function finishPendingIssue() {
        if (!validateCurrentStep()) return;
        syncPendingFromDom();
        if (uploadsMissingForPendingIssue()) {
            showDrawerError(
                t().reportIssueFinaliseUploadsRequired || 'At least one upload is required.'
            );
            return;
        }
        ensureDraft();
        if (!draft) return;

        const toSave = {
            originalSOLineID: pendingIssue.originalSOLineID,
            itemID: pendingIssue.itemID,
            reportingCode: pendingIssue.reportingCode || '',
            lineKey: pendingIssue.lineKey,
            productName: pendingIssue.productName,
            imageUrl: pendingIssue.imageUrl,
            lineQty: pendingIssue.lineQty,
            issueType: pendingIssue.issueType,
            reportedQty: pendingIssue.reportedQty,
            description: pendingIssue.description || '',
            media: pendingIssue.media || [],
            preferredSolution: stepsForPendingIssue().includes('solution')
                ? pendingIssue.preferredSolution ?? null
                : null,
            solution: SHOW_CHOOSE_SOLUTION
                ? pendingIssue.solution ?? null
                : defaultSolutionForIssueType(pendingIssue),
            _localId: pendingIssue._localId,
        };

        if (editingIssueIndex != null && draft.issues[editingIssueIndex]) {
            draft.issues[editingIssueIndex] = toSave;
        } else {
            draft.issues.push(toSave);
        }

        editingIssueIndex = null;
        pendingIssue = null;
        pendingFurthestStep = null;
        pendingIssueReopened = false;
        drawerStep = null;
        drawerContext = null;
        getOrderTrackerDrawer()?.close();
        syncSelectorLineUi();
        if (
            contactSupportIssueIndex != null &&
            draft.issues?.[contactSupportIssueIndex]
        ) {
            showContactSupportView();
        } else {
            showDetailsView();
        }
    }

    /* --- Replacement coverage --- */

    const REPLACEMENT_QTY_BUCKETS = [
        'shipments',
        'preparingForDespatchShipments',
        'outstandingBatches'
    ];

    function sumReplacementOrderLineQuantity(order) {
        const orderItems = order?.orderDetails?.orderItems;
        if (!orderItems) return 0;
        return REPLACEMENT_QTY_BUCKETS.reduce((sum, bucket) => {
            const batches = orderItems[bucket];
            if (!Array.isArray(batches)) return sum;
            return batches.reduce((batchSum, batch) => {
                const items = batch?.items;
                if (!Array.isArray(items)) return batchSum;
                return items.reduce(
                    (itemSum, line) => itemSum + (parseInt(line?.quantity, 10) || 0),
                    batchSum
                );
            }, sum);
        }, 0);
    }

    function soLabelsFromOrderNo(orderNo) {
        const raw = String(orderNo || '').trim();
        if (!raw) return [];
        const matches = raw.match(/SO\d+/gi);
        if (matches && matches.length) return matches.map((match) => match.toUpperCase());
        return [raw];
    }

    function getReplacementCoverage(item) {
        const coverage = { quantity: 0, soNumbers: [] };
        const seen = new Set();
        const replacements = item?.replacements;
        if (!Array.isArray(replacements)) return coverage;

        replacements.forEach((replacement) => {
            const orders = replacement?.orders;
            if (!Array.isArray(orders)) return;
            orders.forEach((order) => {
                const lineQty = sumReplacementOrderLineQuantity(order);
                if (lineQty <= 0) return;
                coverage.quantity += lineQty;
                soLabelsFromOrderNo(order?.orderSummary?.orderNo).forEach((label) => {
                    const key = label.toUpperCase();
                    if (seen.has(key)) return;
                    seen.add(key);
                    coverage.soNumbers.push(label);
                });
            });
        });

        return coverage;
    }

    function getLineItemForIssue(issue) {
        if (!issue?.lineKey) return null;
        return itemRegistry.get(issue.lineKey)?.item || null;
    }

    function isWarrantyExpiredForIssue(issue) {
        const fulfilled = issue?.lineKey
            ? itemRegistry.get(issue.lineKey)?.shipmentOrBatch?.date
            : null;
        if (!(fulfilled instanceof Date) || Number.isNaN(fulfilled.getTime())) return false;

        const expiry = new Date(fulfilled.getTime());
        const day = expiry.getUTCDate();
        expiry.setUTCMonth(expiry.getUTCMonth() + 24);
        if (expiry.getUTCDate() < day) expiry.setUTCDate(0);

        const today = new Date();
        const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
        const expiryUtc = Date.UTC(expiry.getUTCFullYear(), expiry.getUTCMonth(), expiry.getUTCDate());
        return todayUtc > expiryUtc;
    }

    function isReplacementBlockedForIssue(issue) {
        const item = getLineItemForIssue(issue);
        if (!item) return false;
        const ordered = parseInt(item.quantity, 10) || 0;
        if (ordered <= 0) return false;

        return getReplacementCoverage(item).quantity >= ordered;
    }

    function preferredReplacementExceedsQuantity(issue) {
        const item = getLineItemForIssue(issue);
        if (!item) return false;
        const ordered = parseInt(item.quantity, 10) || 0;
        if (ordered <= 0) return false;
        const reported = parseInt(issue?.reportedQty, 10) || 0;
        return getReplacementCoverage(item).quantity + reported > ordered;
    }

    function syncPreferredReplacementNotice() {
        if (!pendingIssue) return false;
        const drawer = getOrderTrackerDrawer();
        const radio = drawer?.querySelector(
            'input[name="report-preferred-solution"][value="replacement"]'
        );
        if (!radio) return false;

        const exceeds = preferredReplacementExceedsQuantity(pendingIssue);
        radio.disabled = exceeds;
        if (exceeds) {
            radio.checked = false;
            if (pendingIssue.preferredSolution === 'replacement') pendingIssue.preferredSolution = null;
        }

        const notice = radio
            .closest('.order-report-issue__step-body')
            ?.querySelector('[data-oa-report-replacement-blocked]');
        const needsChoice = exceeds && pendingIssue.preferredSolution !== 'contact_support';
        if (!notice) return needsChoice;
        if (!exceeds) {
            notice.innerHTML = '';
            setVisiblyHidden(notice, true);
            return false;
        }
        const coverage = getReplacementCoverage(getLineItemForIssue(pendingIssue));
        notice.innerHTML = replacementBlockedNoticeHtml(coverage.soNumbers);
        setVisiblyHidden(notice, false);
        return needsChoice;
    }

    function replacementBlockedNoticeHtml(soNumbers) {
        const tr = t();
        const message = escapeHtml(
            replaceTokens(
                tr.reportIssueReplacementAlreadyRequestedBefore,
                { so_numbers: (soNumbers || []).join(', ') }
            )
        );

        return `${message}`;
    }

    function warrantyExpiredNoticeHtml(solutionType) {
        const tr = t();
        const request =
            solutionType === 'spare_parts'
                ? tr.reportIssueWarrantyExpiredSpareParts || 'spare parts request'
                : tr.reportIssueWarrantyExpiredReplacement || 'replacement request';
        const before = escapeHtml(
            tr.reportIssueWarrantyExpiredBefore ||
                'Your product warranty date for this product has already expired. Please reach out to'
        );
        const linkLabel = escapeHtml(tr.reportIssueWarrantyExpiredLink || 'customer support');
        const after = escapeHtml(
            replaceTokens(tr.reportIssueWarrantyExpiredAfter || 'for a {{ request }}.', { request })
        );
        const url = getReportIssueSupportUrl();
        const linkHtml = url
            ? `<a href="${escapeHtmlAttr(url)}" target="_blank" rel="noopener noreferrer">${linkLabel}</a>`
            : linkLabel;
        return `${before} ${linkHtml} ${after}`;
    }

    function solutionBlockForIssue(issue) {
        if (!issue) return null;
        if (pendingSolutionType !== 'replacement' && pendingSolutionType !== 'spare_parts') return null;
        if (isWarrantyExpiredForIssue(issue)) return 'warranty';
        if (pendingSolutionType === 'replacement' && isReplacementBlockedForIssue(issue)) return 'quantity';
        return null;
    }

    function syncReplacementBlockedNotice() {
        const drawer = getOrderTrackerDrawer();
        const el = drawer?.querySelector('[data-oa-report-replacement-blocked]');
        const confirmBtn = drawer?.querySelector('[data-oa-report-action="solution-confirm"]');
        const issue = draft?.issues?.[solutionIssueIndex];
        const block = solutionBlockForIssue(issue);
        if (confirmBtn) {
            confirmBtn.disabled = Boolean(block);
            confirmBtn.setAttribute('aria-disabled', block ? 'true' : 'false');
        }
        if (!el) return;
        if (!block) {
            el.innerHTML = '';
            setVisiblyHidden(el, true);
            return;
        }
        if (block === 'warranty') {
            el.innerHTML = warrantyExpiredNoticeHtml(pendingSolutionType);
        } else {
            const coverage = getReplacementCoverage(getLineItemForIssue(issue));
            el.innerHTML = replacementBlockedNoticeHtml(coverage.soNumbers);
        }
        setVisiblyHidden(el, false);
    }

    /* --- Solutions drawer --- */

    function renderSolutionProductSummaryHtml(issue) {
        const tr = t();
        const img =
            issue.imageUrl && String(issue.imageUrl).trim()
                ? `<img src="${escapeHtmlAttr(issue.imageUrl)}" alt="" class="order-report-issue__solution-image">`
                : `<div class="order-report-issue__solution-image order-report-issue__solution-image--placeholder"></div>`;
        return `
            <div class="order-report-issue__solution-product">
                ${img}
                <div class="order-report-issue__solution-product-info">
                    <p class="order-report-issue__solution-product-name">${escapeHtml(issue.productName || '')}</p>
                    <p class="order-report-issue__solution-product-meta">${escapeHtml(
                        replaceTokens(tr.reportIssueQuantityLine || 'Quantity: {{ qty }}', {
                            qty: issue.reportedQty,
                        })
                    )}</p>
                    <p class="order-report-issue__solution-product-meta">${
                        replaceTokens(tr.reportIssueIssueLine || 'Issue: {{ issue }}', {
                            issue: labelForIssueType(issue.issueType),
                        })
                    }</p>
                </div>
            </div>`;
    }

    function openSolutionDrawer(issueIndex) {
        const tr = t();
        const issue = draft?.issues?.[issueIndex];
        if (!issue) return;

        solutionIssueIndex = issueIndex;
        pendingSolutionType = issue.solution?.type || null;
        drawerContext = 'solution';
        drawerStep = null;

        const options = solutionOptions()
            .map((opt) => {
                const checked = pendingSolutionType === opt.id ? ' checked' : '';
                return `
                <label class="order-report-issue__option">
                    <input type="radio" name="report-solution-type" value="${escapeHtmlAttr(opt.id)}"${checked}>
                    <span>${escapeHtml(opt.label)}</span>
                </label>`;
            })
            .join('');

        const rootHtml = `
            <div class="order-report-issue-drawer-root order-report-issue-solution-drawer-root">
                ${renderSolutionProductSummaryHtml(issue)}
                <div class="order-report-issue__step-body">
                <p class="order-report-issue__intro">${escapeHtml(tr.reportIssueSolutionsIntro || '')}</p>
                <div class="order-report-issue__options" role="radiogroup">${options}</div>
                <p class="order-report-issue__replacement-blocked" data-oa-report-replacement-blocked role="status" hidden></p>
                </div>
                
                <p class="order-report-issue__field-error" data-oa-report-error hidden></p>
            </div>`;

        const drawer = getOrderTrackerDrawer();
        if (!drawer) return;
        drawer.open({
            title: tr.reportIssueSolutionsTitle || 'Solutions',
            html: rootHtml,
            footerHtml: footerButtonsHtml(
                'solution-confirm',
                tr.reportIssueSolutionConfirm || 'Confirm',
                'solution-back',
                tr.reportIssueBack || 'Back'
            ),
        });
        syncReplacementBlockedNotice();
    }

    function confirmSolutionSelection() {
        const tr = t();
        if (!pendingSolutionType) {
            showDrawerError(tr.reportIssueSolutionRequired || 'Please select a solution.');
            return;
        }
        if (solutionIssueIndex == null || !draft?.issues?.[solutionIssueIndex]) return;

        const selectedType = pendingSolutionType;
        const issueIndex = solutionIssueIndex;

        if (
            (selectedType === 'replacement' || selectedType === 'spare_parts') &&
            isWarrantyExpiredForIssue(draft.issues[issueIndex])
        ) {
            syncReplacementBlockedNotice();
            return;
        }
        if (
            selectedType === 'replacement' &&
            isReplacementBlockedForIssue(draft.issues[issueIndex])
        ) {
            syncReplacementBlockedNotice();
            return;
        }
        const payload = solutionTypePayload(selectedType);
        const prev = draft.issues[issueIndex].solution;
        const solutionPayload = {
            type: selectedType,
            typeID: solutionTypeId(selectedType),
            tag: payload?.tag || '',
            name: payload?.name || '',
        };
        if (selectedType === 'spare_parts' && prev?.type === 'spare_parts') {
            if (prev.componentQty && typeof prev.componentQty === 'object') {
                solutionPayload.componentQty = prev.componentQty;
            }
            if (prev.selectedFixings && typeof prev.selectedFixings === 'object') {
                solutionPayload.selectedFixings = prev.selectedFixings;
            }
            if (prev.fixingSnapshots && typeof prev.fixingSnapshots === 'object') {
                solutionPayload.fixingSnapshots = prev.fixingSnapshots;
            }
        }

        solutionIssueIndex = null;
        pendingSolutionType = null;
        drawerContext = null;
        getOrderTrackerDrawer()?.close();

        if (selectedType === 'replacement') {
            // Not committed to draft.issues yet — only continueResendSelection() writes it,
            // once disposition + cartons are actually chosen. This way clicking Back on the
            // Resend view leaves the issue's previous solution (or none) untouched.
            openResendView(issueIndex, solutionPayload);
            return;
        }

        draft.issues[issueIndex].solution = solutionPayload;

        if (selectedType === 'contact_support') {
            openContactSupportView(issueIndex);
            return;
        }

        if (selectedType === 'spare_parts') {
            openSparePartsView(issueIndex);
            return;
        }

        showDetailsView();
    }

    function closeSolutionDrawer() {
        solutionIssueIndex = null;
        pendingSolutionType = null;
        drawerContext = null;
        getOrderTrackerDrawer()?.close();
    }

    /* --- Contact Support review --- */

    function syncContactSupportCommentsFromDom() {
        const ta = getStepEl()?.querySelector('[data-oa-report-contact-comments]');
        if (!ta || contactSupportIssueIndex == null || !draft?.issues?.[contactSupportIssueIndex]) {
            return;
        }
        draft.issues[contactSupportIssueIndex].description = ta.value || '';
    }

    function openContactSupportView(issueIndex) {
        if (!draft?.issues?.[issueIndex]) return;
        contactSupportIssueIndex = issueIndex;
        showContactSupportView();
    }

    function showContactSupportView() {
        view = 'contact';
        setVisiblyHidden(getShipmentsContainer(), true);
        setVisiblyHidden(getStepEl(), false);
        if (draft) {
            draft.ui = {
                ...(draft.ui || {}),
                step: 'contact',
                activeIssueIndex: contactSupportIssueIndex,
            };
        }
        updateHeaderActions();
        renderContactSupportPanel();
    }

    function exitContactSupportView() {
        contactSupportIssueIndex = null;
        showDetailsView();
    }

    function selectContactSupportSolution() {
        syncContactSupportCommentsFromDom();
        contactSupportIssueIndex = null;
        showDetailsView();
    }

    function renderContactSupportPanel() {
        const tr = t();
        const stepEl = getStepEl();
        const index = contactSupportIssueIndex;
        const issue = draft?.issues?.[index];
        if (!stepEl || !issue) return;

        const img =
            issue.imageUrl && String(issue.imageUrl).trim()
                ? `<img src="${escapeHtmlAttr(issue.imageUrl)}" alt="" class="order-report-issue__card-image">`
                : `<div class="order-report-issue__card-image order-report-issue__card-image--placeholder"></div>`;

        const thumbs = (issue.media || [])
            .map(
                (m, mi) => `
            <div class="order-report-issue__thumb">
                ${
                    m.type === 'video'
                        ? `<span class="order-report-issue__thumb-video">▶</span>`
                        : `<img src="${escapeHtmlAttr(mediaThumbSrc(m))}" alt="">`
                }
                <button type="button" class="order-report-issue__thumb-remove" data-oa-report-action="remove-card-media" data-oa-report-issue-index="${index}" data-media-index="${mi}" aria-label="Remove">&times;</button>
            </div>`
            )
            .join('');

        const solutionHtml = `
            <div class="order-report-issue__card-solution">
                <span>${escapeHtml(
                    replaceTokens(tr.reportIssueSolutionLine || 'Solution: {{ solution }}', {
                        solution: labelForSolutionType(issue.solution?.type),
                    })
                )}</span>
                <button type="button" class="button order-report-issue__choose-solution" data-oa-report-action="choose-solution" data-oa-report-issue-index="${index}">
                    ${CHANGE_ICON_SVG}
                    <span>${escapeHtml(tr.reportIssueChange)}</span>
                </button>
            </div>`;

        stepEl.innerHTML = `
            <div class="order-report-issue-contact-root">
                <div class="order-report-issue__card" data-oa-report-issue-index="${index}">
                    <div class="order-report-issue__card-main">
                        ${img}
                        <div class="order-report-issue__card-info">
                            <p class="order-report-issue__card-name">${escapeHtml(issue.productName || '')}</p>
                            <p class="order-report-issue__card-meta">${escapeHtml(
                                replaceTokens(tr.reportIssueQuantityLine || 'Quantity: {{ qty }}', {
                                    qty: issue.reportedQty,
                                })
                            )}</p>
                            <p class="order-report-issue__card-meta">${
                                replaceTokens(tr.reportIssueIssueLine || 'Issue: {{ issue }}', {
                                    issue: labelForIssueType(issue.issueType),
                                })
                            }</p>
                        </div>
                        <div class="order-report-issue__card-actions">
                            <button type="button" class="order-report-issue__card-remove" data-oa-report-action="remove-issue" data-oa-report-issue-index="${index}" aria-label="Remove">&times;</button>
                            <button type="button" class="button order-report-issue__card-edit" data-oa-report-action="edit-issue" data-oa-report-issue-index="${index}">
                                ${EDIT_ICON_SVG}
                                <span>${escapeHtml(tr.reportIssueEdit)}</span>
                            </button>
                        </div>
                    </div>
                    ${
                        thumbs
                            ? `<div class="order-report-issue__card-uploads"><p>${escapeHtml(
                                  tr.reportIssueUploadsSummary || 'Uploads'
                              )}</p><div class="order-report-issue__thumbs">${thumbs}</div></div>`
                            : ''
                    }
                    <div class="order-report-issue__contact-comments">
                        <p class="order-report-issue__contact-comments-label">${escapeHtml(
                            commentsLabelForIssueType(issue.issueType)
                        )}</p>
                        <textarea class="order-report-issue__comments" data-oa-report-contact-comments rows="5" placeholder="${escapeHtmlAttr(
                            tr.reportIssueCommentsPlaceholder ||
                                'Describe the issue in more detail (optional)'
                        )}">${escapeHtml(issue.description || '')}</textarea>
                    </div>
                    ${solutionHtml}
                </div>
            </div>`;
    }

    /* --- Resend Order (Replacements) --- */

    function getOrderAmendApiUrlBase() {
        const raw = (getRoot()?.dataset.orderAmendApiUrl || '').trim();
        return raw.toLowerCase().startsWith('https://') ? raw : '';
    }

    function buildOrderAmendApiUrl() {
        const base = getOrderAmendApiUrlBase();
        const apiKey = getClientApiKey();
        if (!base || !apiKey) return '';
        try {
            const url = new URL(base);
            url.searchParams.set('clientApiKey', apiKey);
            return url.toString();
        } catch {
            return '';
        }
    }

    function resendProductKey(product) {
        return String(product?.productId || product?.id || '');
    }

    function normalizeResendProduct(raw, index) {
        const reportingCode = String(raw?.reporting_code ?? raw?.reportingCode ?? '');
        const productId = String(raw?.product_id ?? raw?.productId ?? raw?.id ?? `item_${index}`);
        const itemNumber = String(raw?.item_number ?? raw?.itemNumber ?? '');
        const leg = raw?.leg != null && raw.leg !== '' ? String(raw.leg) : String(index + 1);
        const inStock = !reportingCode.toUpperCase().includes('OUT');
        const unitPrice = parseNumber(raw?.unitPrice);
        const tr = t();
        const name = String(
            raw?.product_name ?? raw?.productName ?? raw?.name ?? raw?.title ?? ''
        ).trim();

        return {
            id: productId,
            productId,
            itemNumber,
            reportingCode,
            leg,
            name,
            inStock,
            unitPrice,
        };
    }

    function extractResendProducts({ data, success }) {
        const list = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data?.products)
                ? data.products
                : Array.isArray(data?.items)
                  ? data.items
                  : Array.isArray(data?.relatedProducts)
                    ? data.relatedProducts
                    : [];

        return list.map((item, i) => normalizeResendProduct(item, i));
    }

    async function fetchResendRelatedProducts(reportingCode) {
        const tr = t();
        const url = buildOrderAmendApiUrl();
        if (!url) {
            throw new Error(tr.reportIssueResendError || 'We could not load replacement options. Please try again.');
        }
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: 'getResendRelatedProducts',
                reportingCode,
            }),
        });
        const data = await res.json().catch(() => ({}));
        const innerFailed = data?.data && typeof data.data === 'object' && !Array.isArray(data.data) && data.data.success === false;
        if (!res.ok || data.success === false || innerFailed) {
            throw new Error(
                data.data?.errorMessage ||
                    data.data?.message ||
                    data.errorMessage ||
                    data.message ||
                    tr.reportIssueResendError ||
                    'We could not load replacement options. Please try again.'
            );
        }
        return extractResendProducts(data);
    }

    async function fetchResendAvailability(items) {
        const tr = t();
        const url = buildOrderAmendApiUrl();
        if (!url) {
            throw new Error(tr.reportIssueResendError || 'We could not load replacement options. Please try again.');
        }
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
                action: 'getAvailability',
                items,
            }),
        });
        const data = await res.json().catch(() => ({}));
        const availabilityItems = data?.data?.items ?? data?.items;
        if (!res.ok || data.success === false || !Array.isArray(availabilityItems)) {
            throw new Error(tr.reportIssueResendError || 'We could not load replacement options. Please try again.');
        }
        return availabilityItems;
    }

    async function applyResendAvailability(products, quantity) {
        const withItemNumber = products.filter((p) => p.itemNumber);
        if (withItemNumber.length === 0) return [];

        const availabilityItems = await fetchResendAvailability(
            withItemNumber.map((p) => ({ itemNumber: p.itemNumber, productId: p.productId, quantity }))
        );

        const estimateByItemNumber = new Map(
            availabilityItems
                .filter((item) => item && item.itemNumber != null)
                .map((item) => [String(item.itemNumber), item.availabilityEstimate])
        );

        return withItemNumber.reduce((resolved, product) => {
            const estimate = estimateByItemNumber.get(product.itemNumber);
            const label = String(estimate?.label ?? '').trim();
            if (!estimate || (!label && !estimate.inStockNow)) return resolved;

            resolved.push({
                ...product,
                inStock: true,
                inStockNow: Boolean(estimate.inStockNow),
                deliveryDate: estimate.availabilityDate || '',
                deliveryDateText: label,
            });
            return resolved;
        }, []);
    }

    function formatResendDeliveryDate(isoString) {
        if (!isoString) return '';
        const date = new Date(isoString);
        if (Number.isNaN(date.getTime())) return '';

        const monthNamesShort = t().monthNamesShort;
        if (!Array.isArray(monthNamesShort) || !monthNamesShort[date.getUTCMonth()]) return '';

        const day = date.getUTCDate();
        const month = monthNamesShort[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        const currentYear = new Date().getUTCFullYear();

        return year === currentYear ? `${day} ${month}` : `${day} ${month} ${year}`;
    }

    function resendAvailabilityLabel(product) {
        const tr = t();
        if (product.inStockNow) return tr.reportIssueResendInStock || 'In stock';
        const date = formatResendDeliveryDate(product.deliveryDate) || product.deliveryDateText || '';
        return replaceTokens(tr.reportIssueResendDeliveryLabel || 'Estimated to get it by {{ date }}', { date });
    }

    /* --- Replacement availability (Didn't receive product) --- */

    function usesReplacementCheck(issue) {
        return !SHOW_CHOOSE_SOLUTION && issue?.issueType === 'didnt_receive_product';
    }

    function replacementCheckKey(issue) {
        return `${issue?.reportingCode || ''}|${parseInt(issue?.reportedQty, 10) || 0}`;
    }

    async function fetchReplacementAvailability(reportingCode, quantity) {
        const url = buildOrderAmendApiUrl();
        if (!url) throw new Error('getAvailability: order amend API URL or client API key missing');
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
                action: 'getAvailability',
                quantity,
                reportingCode,
            }),
        });
        const data = await res.json().catch(() => ({}));
        const availability = data?.data;
        const valid = availability && typeof availability === 'object' && typeof availability.inStock === 'boolean';
        if (!res.ok || data.success === false || !valid) {
            throw new Error(`getAvailability failed (${res.status}): ${data?.errorMessage || data?.message || 'invalid response'}`);
        }
        return availability;
    }


    function pickReplacementAvailability(availability) {
        if (availability.inStock) return { inStockNow: true, availabilityDate: null };
        const date = global.OrderDeliveryEstimate.parseDate(availability.availabilityDate);
        return date ? { inStockNow: false, availabilityDate: date } : null;
    }

    function ensureReplacementCheck() {
        const issue = pendingIssue;
        if (!usesReplacementCheck(issue)) return;
        const key = replacementCheckKey(issue);
        if (issue.replacementCheck?.key === key) return;

        issue.replacementCheck = { key, status: 'loading' };
        if (!issue.reportingCode || !global.OrderDeliveryEstimate) {
            console.error('Report issue: replacement availability not checked — missing reportingCode or OrderDeliveryEstimate');
            issue.replacementCheck = { key, status: 'error' };
            return;
        }

        fetchReplacementAvailability(issue.reportingCode, parseInt(issue.reportedQty, 10) || 1)
            .then((availability) => {
                const pick = pickReplacementAvailability(availability);
                if (!pick) return { key, status: 'none' };
                const estimate = global.OrderDeliveryEstimate.calculate(pick);
                if (!estimate.success) throw new Error('getAvailability: could not calculate delivery dates');
                return {
                    key,
                    status: 'ready',
                    inStockNow: estimate.inStockNow,
                    minDeliveryDateIso: estimate.minDeliveryDateIso,
                    maxDeliveryDateIso: estimate.maxDeliveryDateIso,
                };
            })
            .catch((err) => {
                console.error('Report issue: replacement availability check failed', err);
                return { key, status: 'error' };
            })
            .then((result) => applyReplacementCheck(issue, result));
    }

    function applyReplacementCheck(issue, result) {
        // Ignore late answers — drawer closed or quantity changed.
        if (pendingIssue !== issue || issue.replacementCheck?.key !== result.key) return;
        issue.replacementCheck = result;
        if (result.status === 'none') issue.preferredSolution = null;
        if (drawerContext !== 'issue') return;

        if (drawerStep === 'review') {
            openDrawerStep('review');
            return;
        }
        if (drawerStep === 'solution') {
            if (result.status === 'none') {
                // No replacement — save as Contact Support.
                drawerStep = 'comments';
                finishPendingIssue();
                return;
            }
            syncPendingFromDom();
            openDrawerStep('solution');
            return;
        }
        refreshDrawerStepChrome();
    }

    function refreshDrawerStepChrome() {
        const drawer = getOrderTrackerDrawer();
        if (!drawer || !pendingIssue) return;
        const tr = t();
        const steps = stepsForPendingIssue();
        const isLast = drawerStep === steps[steps.length - 1];

        const primary = drawer.querySelector('[data-oa-report-action="drawer-next"], [data-oa-report-action="drawer-finish"]');
        if (primary) {
            primary.dataset.oaReportAction = isLast ? 'drawer-finish' : 'drawer-next';
            primary.textContent = isLast ? tr.reportIssueFinish || 'Finish' : tr.reportIssueNext || 'Next';
        }
        const percent = progressPercent();
        drawer.querySelector('.order-report-issue__progress')?.setAttribute('aria-valuenow', String(percent));
        const fill = drawer.querySelector('.order-report-issue__progress-fill');
        if (fill) fill.style.width = `${percent}%`;
    }

    function replacementDeliveryEstimate(issue) {
        const check = issue?.replacementCheck;
        if (check?.status !== 'ready') return null;
        return {
            inStockNow: Boolean(check.inStockNow),
            minDeliveryDateIso: check.minDeliveryDateIso,
            maxDeliveryDateIso: check.maxDeliveryDateIso,
        };
    }

    function replacementDeliveryRange(issue) {
        const estimate = replacementDeliveryEstimate(issue);
        if (!estimate || !global.OrderDeliveryEstimate) return '';
        return global.OrderDeliveryEstimate.formatDateRange(estimate.minDeliveryDateIso, estimate.maxDeliveryDateIso);
    }

    function openResendView(issueIndex, pendingSolutionPayload = null) {
        if (!draft?.issues?.[issueIndex]) return;
        resendIssueIndex = issueIndex;
        resendPendingSolution = pendingSolutionPayload;
        const existing = draft.issues[issueIndex].solution || {};
        const quantities = {};
        const maxQty = Math.max(1, parseInt(draft.issues[issueIndex]?.reportedQty, 10) || 1);
        (existing.resendItems || []).some((item) => {
            const key = String(item?.productId || item?.id || '');
            const quantity = parseInt(item?.quantity, 10) || 0;
            if (!key || quantity <= 0) return false;
            quantities[key] = Math.min(maxQty, quantity);
            return true;
        });
        resendState = {
            ...createEmptyResendState(),
            disposition: existing.disposition || null,
            quantities,
            loading: true,
        };
        showResendView();
        loadResendProducts(issueIndex);
    }

    async function loadResendProducts(issueIndex) {
        const tr = t();
        const issue = draft?.issues?.[issueIndex];
        if (!issue) return;

        let reportingCode = issue.reportingCode || '';
        if (!reportingCode && issue.lineKey) {
            const entry = itemRegistry.get(issue.lineKey);
            if (entry?.item?.reportingCode != null) {
                reportingCode = String(entry.item.reportingCode);
                issue.reportingCode = reportingCode;
            }
        }

        resendState.loading = true;
        resendState.error = '';
        resendState.noneAvailable = false;
        renderResendPanel();

        try {
            const relatedProducts = await fetchResendRelatedProducts(reportingCode);
            if (resendIssueIndex !== issueIndex) return;
            const products = relatedProducts.length
                ? await applyResendAvailability(relatedProducts, getResendMaxQty())
                : [];
            if (resendIssueIndex !== issueIndex) return;
            resendState.products = products;
            resendState.noneAvailable = relatedProducts.length > 0 && products.length === 0;
            resendState.loading = false;
            renderResendPanel();
        } catch (err) {
            if (resendIssueIndex !== issueIndex) return;
            console.error(err);
            resendState.loading = false;
            resendState.error =
                err?.message ||
                tr.reportIssueResendError ||
                'We could not load replacement options. Please try again.';
            renderResendPanel();
        }
    }

    function showResendView() {
        view = 'resend';
        setVisiblyHidden(getShipmentsContainer(), true);
        setVisiblyHidden(getStepEl(), false);
        if (draft) draft.ui = { ...(draft.ui || {}), step: 'resend', activeIssueIndex: resendIssueIndex };
        updateHeaderActions();
        renderResendPanel();
    }

    function renderResendCartonHtml(product) {
        const tr = t();
        const key = resendProductKey(product);
        const qty = parseInt(resendState.quantities[key], 10) || 0;
        const selected = qty > 0;
        const anotherSelected = Object.keys(resendState.quantities || {}).some((qtyKey) => {
            if (qtyKey === key) return false;
            return (parseInt(resendState.quantities[qtyKey], 10) || 0) > 0;
        });
        const maxQty = getResendMaxQty();
        const atMaxQty = qty >= maxQty;
        const stockLabel = product.inStock
            ? resendAvailabilityLabel(product)
            : tr.reportIssueResendOutOfStock;
        const stockClass = product.inStock ? '' : ' order-report-issue__resend-carton-stock--oos';

        const actionHtml = !product.inStock
            ? `<button type="button" class="order-report-issue__resend-action order-report-issue__resend-add order-report-issue__resend-add--disabled" disabled aria-disabled="true">${escapeHtml(
                  tr.reportIssueResendUnavailable || 'Unavailable'
              )}</button>`
            : selected
              ? `<div class="order-report-issue__resend-actions">
                    <div class="order-report-issue__resend-qty" data-oa-report-resend-code="${escapeHtmlAttr(key)}">
                        <button type="button" data-oa-report-action="resend-qty-dec" data-oa-report-resend-code="${escapeHtmlAttr(
                            key
                        )}" aria-label="Decrease"${qty <= 1 ? ' disabled aria-disabled="true"' : ''}>−</button>
                        <span class="order-report-issue__resend-qty-value">${qty}</span>
                        <button type="button" data-oa-report-action="resend-qty-inc" data-oa-report-resend-code="${escapeHtmlAttr(
                            key
                        )}" aria-label="Increase"${atMaxQty ? ' disabled aria-disabled="true"' : ''}>+</button>
                    </div>
                    <button type="button" class="order-report-issue__resend-action order-report-issue__resend-undo" data-oa-report-action="resend-undo" data-oa-report-resend-code="${escapeHtmlAttr(
                        key
                    )}">${escapeHtml(tr.reportIssueResendUndo || 'Undo')}</button>
                </div>`
              : `<button type="button" class="order-report-issue__resend-action order-report-issue__resend-add${
                    anotherSelected ? ' order-report-issue__resend-add--disabled' : ''
                }" data-oa-report-action="resend-add" data-oa-report-resend-code="${escapeHtmlAttr(key)}"${
                    anotherSelected ? ' disabled aria-disabled="true"' : ''
                }>${escapeHtml(tr.reportIssueResendAdd || 'Add')}</button>`;

        return `
            <div class="order-report-issue__resend-carton">
                <div>
                    <p class="order-report-issue__resend-carton-name">${escapeHtml(product.name)}</p>
                    <p class="order-report-issue__resend-carton-stock${stockClass}">${escapeHtml(stockLabel)}</p>
                </div>
                ${actionHtml}
            </div>`;
    }

    function renderResendPanel() {
        const tr = t();
        const stepEl = getStepEl();
        const issue = draft?.issues?.[resendIssueIndex];
        const index = resendIssueIndex;
        if (!stepEl || !issue) return;

        const img =
            issue.imageUrl && String(issue.imageUrl).trim()
                ? `<img src="${escapeHtmlAttr(issue.imageUrl)}" alt="" class="order-report-issue__resend-product-image">`
                : `<div class="order-report-issue__resend-product-image order-report-issue__resend-product-image--placeholder"></div>`;

        const disposition = resendState.disposition;
        const cartonsHtml = resendState.loading
            ? `<p class="order-report-issue__resend-loading">${escapeHtml(
                  tr.reportIssueResendLoading || 'Loading replacement options…'
              )}</p>`
            : resendState.error
              ? `<div class="order-report-issue__panel-error">${validationErrorBannerHtml(
                    resendState.error
                )}</div>`
              : resendState.products.length
                ? `<div class="order-report-issue__resend-carton-list">${resendState.products
                      .map((p) => renderResendCartonHtml(p))
                      .join('')}</div>`
                : `<p class="order-report-issue__resend-empty">${escapeHtml(
                      resendState.noneAvailable
                          ? tr.reportIssueResendNoneAvailable ||
                                'No replacement parts are currently available for this item.'
                          : tr.reportIssueResendError || 'We could not load replacement options. Please try again.'
                  )}</p>`;

        const fieldError = resendState.fieldError
            ? `<p class="order-report-issue__resend-field-error" role="alert">${escapeHtml(
                  resendState.fieldError
              )}</p>`
            : '';

        stepEl.innerHTML = `
            <div class="order-report-issue-resend-card">
                <div class="order-report-issue__resend-section-head">
                    <p class="order-report-issue__resend-section-title">${escapeHtml(
                        tr.reportIssueResendProductOrdered || 'Product ordered'
                    )}</p>
                    <p class="order-report-issue__resend-order-no">${escapeHtml(
                        replaceTokens(tr.reportIssueResendOrderLabel || 'Order {{ order }}', {
                            order: getOrderNumber(),
                        })
                    )}</p>
                </div>
                <div class="order-report-issue__card${
                    dispositionHiddenForIssueType(issue.issueType) ? ' order-report-issue__card--no-disposition' : ''
                }" data-oa-report-issue-index="${index}">
                    <div class="order-report-issue__card-main">
                        ${img}
                        <div class="order-report-issue__card-info">
                            <p class="order-report-issue__card-name">${escapeHtml(issue.productName || '')}</p>
                            <p class="order-report-issue__card-meta">${escapeHtml(
                                replaceTokens(tr.reportIssueQuantityLine || 'Quantity: {{ qty }}', {
                                    qty: issue.reportedQty,
                                })
                            )}</p>
                            <p class="order-report-issue__card-meta">${
                                replaceTokens(tr.reportIssueIssueLine || 'Issue: {{ issue }}', {
                                    issue: labelForIssueType(issue.issueType),
                                })
                            }</p>
                        </div>
                        <div class="order-report-issue__card-actions">
                            <button type="button" class="order-report-issue__card-remove" data-oa-report-action="remove-issue" data-oa-report-issue-index="${index}" aria-label="Remove">&times;</button>
                        </div>
                    </div>
                </div>
                ${
                    dispositionHiddenForIssueType(issue.issueType)
                        ? ''
                        : `<div class="order-report-issue__resend-disposition">
                    <p class="order-report-issue__resend-disposition-label">${escapeHtml(
                        tr.reportIssueResendDispositionLabel ||
                            'Would you like to keep or return the original product?'
                    )}</p>
                    <div class="order-report-issue__resend-radios" role="radiogroup">
                        <label class="order-report-issue__resend-radio">
                            <input type="radio" name="report-resend-disposition" value="return"${
                                disposition === 'return' ? ' checked' : ''
                            }>
                            <span>${escapeHtml(tr.reportIssueResendReturn || 'Return')}</span>
                        </label>
                        <label class="order-report-issue__resend-radio">
                            <input type="radio" name="report-resend-disposition" value="keep"${
                                disposition === 'keep' ? ' checked' : ''
                            }>
                            <span>${escapeHtml(tr.reportIssueResendKeep || 'Keep')}</span>
                        </label>
                    </div>
                </div>`
                }
                <p class="order-report-issue__resend-cartons-title">${escapeHtml(
                    tr.reportIssueResendSelectCartons || 'Select which product you require'
                )}</p>
                ${cartonsHtml}
                ${fieldError}
            </div>`;
        updateHeaderActions();
    }

    function getResendMaxQty() {
        const reportedQty = parseInt(draft?.issues?.[resendIssueIndex]?.reportedQty, 10);
        return reportedQty > 0 ? reportedQty : 1;
    }

    function setResendQuantity(code, qty) {
        const next = Math.min(getResendMaxQty(), Math.max(0, parseInt(qty, 10) || 0));
        if (next <= 0) {
            delete resendState.quantities[code];
        } else {
            resendState.quantities[code] = next;
        }
        resendState.fieldError = '';
        renderResendPanel();
    }

    function exitResendView() {
        // Nothing to clean up here: confirmSolutionSelection() no longer writes the
        // replacement solution to draft.issues up front, so backing out simply discards
        // the in-progress selection without touching the issue's previous solution.
        resendIssueIndex = null;
        resendPendingSolution = null;
        resendState = createEmptyResendState();
        showDetailsView();
    }

    function continueResendSelection() {
        const tr = t();
        const issue = draft?.issues?.[resendIssueIndex];
        if (!issue) return;

        resendState.fieldError = '';
        if (!resendState.disposition && !dispositionHiddenForIssueType(issue.issueType)) {
            resendState.fieldError =
                tr.reportIssueResendDispositionRequired || 'Please choose keep or return.';
            renderResendPanel();
            return;
        }

        const resendItems = resendState.products
            .map((p) => {
                const quantity = Math.min(
                    getResendMaxQty(),
                    parseInt(resendState.quantities[resendProductKey(p)], 10) || 0
                );
                if (quantity <= 0) return null;
                return {
                    productId: p.productId,
                    itemNumber: p.itemNumber,
                    reportingCode: p.reportingCode,
                    leg: p.leg,
                    quantity,
                    inStock: p.inStock,
                    unitPrice: parseNumber(p.unitPrice),
                };
            })
            .filter(Boolean);

        if (!resendItems.length) {
            resendState.fieldError =
                tr.reportIssueResendCartonsRequired || 'Please select at least one carton.';
            renderResendPanel();
            return;
        }

        issue.solution = {
            ...(resendPendingSolution || issue.solution),
            disposition: dispositionHiddenForIssueType(issue.issueType) ? null : resendState.disposition,
            resendItems,
        };
        resendIssueIndex = null;
        resendPendingSolution = null;
        resendState = createEmptyResendState();
        showDetailsView();
    }

    /* --- Spare Parts --- */
    const SPARES_THUMB_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.2"/><circle cx="8.5" cy="10" r="1.4" fill="currentColor"/><path d="M4 16.5l4.2-4.2 2.8 2.8 3.2-3.6L20 16.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const ADD_FIXINGS_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M5.14551 0C5.47654 0.000239712 5.74588 0.268566 5.74609 0.599609V4.54688H9.40039C9.73145 4.54709 9.99979 4.81542 10 5.14648C10 5.47773 9.73158 5.74686 9.40039 5.74707H5.74609V9.40039C5.74588 9.73143 5.47654 9.99976 5.14551 10C4.81445 9.99979 4.54611 9.73145 4.5459 9.40039V5.74707H0.599609C0.268418 5.74686 4.43327e-07 5.47772 0 5.14648C0.000210969 4.81542 0.268548 4.54709 0.599609 4.54688H4.5459V0.599609C4.54611 0.268548 4.81445 0.000210926 5.14551 0Z" fill="#424442"/></svg>';

    function sparePartKey(part) {
        return String(part?.itemNumber || part?.itemId || '').trim();
    }

    function sparePartMaxQty(part) {
        const n = parseInt(part?.sparePartQuantity, 10);
        return Number.isFinite(n) && n >= 1 ? n : 1;
    }

    function countQtyMap(map) {
        return Object.values(map || {}).filter((n) => (parseInt(n, 10) || 0) > 0).length;
    }

    function selectedSpareCount() {
        return countQtyMap(sparePartsState.componentQty) + countQtyMap(sparePartsState.selectedFixings);
    }

    function qtyMapForScope(scope) {
        if (scope === 'component') return sparePartsState.componentQty;
        if (scope === 'selected') return sparePartsState.selectedFixings;
        return sparePartsState.drawerFixingQty;
    }

    function findSparePart(scope, key) {
        const list = scope === 'component' ? sparePartsState.components : sparePartsState.fixings;
        const found = (list || []).find((part) => sparePartKey(part) === key);
        if (found) return found;
        return sparePartsState.fixingSnapshots?.[key] || null;
    }

    function restoreSpareQtyFromIssue(issue) {
        const componentQty = issue?.solution?.componentQty;
        const selectedFixings = issue?.solution?.selectedFixings;
        const snapshots = issue?.solution?.fixingSnapshots;
        if (componentQty && typeof componentQty === 'object') {
            sparePartsState.componentQty = { ...componentQty };
        }
        if (selectedFixings && typeof selectedFixings === 'object') {
            sparePartsState.selectedFixings = { ...selectedFixings };
        }
        if (snapshots && typeof snapshots === 'object') {
            sparePartsState.fixingSnapshots = { ...snapshots };
        }
    }

    function persistSpareQtyToIssue() {
        const issue = draft?.issues?.[sparePartsIssueIndex];
        if (!issue?.solution) return;
        issue.solution.componentQty = { ...sparePartsState.componentQty };
        issue.solution.selectedFixings = { ...sparePartsState.selectedFixings };
        issue.solution.fixingSnapshots = { ...sparePartsState.fixingSnapshots };
        flushPersist();
    }

    function hideReportIssueAddressEmbed() {
        global.OrderTrackerAddressUpdate?.hideAddressPanel?.();
    }

    function persistSubmitAddressSnapshot() {
        const snap = global.OrderTrackerAddressUpdate?.getFormSnapshot?.();
        if (snap && typeof snap === 'object' && draft) {
            draft.shippingAddress = { ...snap };
        }
    }

    function snapshotSelectedFixings() {
        const snaps = { ...(sparePartsState.fixingSnapshots || {}) };
        Object.keys(sparePartsState.selectedFixings || {}).forEach((key) => {
            const part = findSparePart('drawer', key);
            if (part) snaps[key] = part;
        });
        sparePartsState.fixingSnapshots = snaps;
    }

    function unwrapAmendDataList(payload) {
        const data = payload?.data;
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.data)) return data.data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    }

    function isAmendPayloadFailed(res, payload) {
        const innerFailed =
            payload?.data &&
            typeof payload.data === 'object' &&
            !Array.isArray(payload.data) &&
            payload.data.success === false;
        return !res.ok || payload?.success === false || innerFailed;
    }

    function amendPayloadErrorMessage(payload, fallback) {
        return (
            payload?.data?.errorMessage ||
            payload?.data?.message ||
            payload?.errorMessage ||
            payload?.message ||
            fallback
        );
    }

    function normalizeSpareComponent(raw) {
        return {
            itemId: String(raw?.itemId ?? ''),
            itemNumber: String(raw?.itemNumber ?? ''),
            assemblyItemNumber: String(raw?.assemblyItemNumber ?? ''),
            title: String(raw?.spareDescription ?? raw?.itemDescription ?? ''),
            sparePartQuantity: raw?.sparePartQuantity,
            stockStatus: 'loading',
            kind: 'component',
        };
    }

    function normalizeSpareFixing(raw) {
        return {
            itemId: String(raw?.itemId ?? ''),
            itemNumber: String(raw?.itemNumber ?? ''),
            title: String(raw?.itemDescription ?? raw?.spareDescription ?? ''),
            sparePartQuantity: raw?.sparePartQuantity,
            stockStatus: 'loading',
            kind: 'fixing',
        };
    }

    function extractAssemblyParts(payload, collectionKey, normalize) {
        const parts = [];
        unwrapAmendDataList(payload).forEach((assembly) => {
            (Array.isArray(assembly?.[collectionKey]) ? assembly[collectionKey] : []).forEach((part) => {
                parts.push(normalize(part));
            });
        });
        return parts;
    }

    function extractSparePartsLookup(payload) {
        return extractAssemblyParts(payload, 'spareParts', normalizeSpareComponent);
    }

    function extractFixingsLookup(payload) {
        return extractAssemblyParts(payload, 'fixings', normalizeSpareFixing);
    }

    function collectPartSkuNumbers(parts) {
        const seen = new Set();
        const numbers = [];
        (parts || []).forEach((part) => {
            const sku = String(part?.itemNumber || '').trim();
            if (!sku || seen.has(sku)) return;
            seen.add(sku);
            numbers.push(sku);
        });
        return numbers;
    }

    function resolveIssueItemNumber(issue) {
        let itemNumber = issue?.itemID != null ? String(issue.itemID).trim() : '';
        if (!itemNumber && issue?.lineKey) {
            const entry = itemRegistry.get(issue.lineKey);
            if (entry?.item?.itemNumber != null) {
                itemNumber = String(entry.item.itemNumber).trim();
                issue.itemID = itemNumber;
            }
        }
        return itemNumber;
    }

    function cloneCachedSpareParts(parts) {
        return (Array.isArray(parts) ? parts : []).map((part) => ({
            ...part,
            stockStatus: 'loading',
        }));
    }

    function readSpareLookupCache(action, itemNumber) {
        const sku = String(itemNumber || '').trim();
        if (!action || !sku) return null;
        try {
            const raw = global.localStorage.getItem(SPARE_LOOKUP_CACHE_KEY);
            if (!raw) return null;
            const all = JSON.parse(raw);
            const entry = all?.[`${action}:${sku}`];
            if (!entry || !Array.isArray(entry.parts) || !entry.timestamp) return null;
            if (Date.now() - entry.timestamp > SPARE_LOOKUP_TTL_MS) return null;
            return cloneCachedSpareParts(entry.parts);
        } catch {
            return null;
        }
    }

    function writeSpareLookupCache(action, itemNumber, parts) {
        const sku = String(itemNumber || '').trim();
        if (!action || !sku || !Array.isArray(parts)) return;
        try {
            const raw = global.localStorage.getItem(SPARE_LOOKUP_CACHE_KEY);
            const all = raw ? JSON.parse(raw) : {};
            all[`${action}:${sku}`] = {
                timestamp: Date.now(),
                parts: parts.map((part) => ({ ...part })),
            };
            global.localStorage.setItem(SPARE_LOOKUP_CACHE_KEY, JSON.stringify(all));
        } catch {
            /* localStorage may be unavailable */
        }
    }

    async function postOrderAmendAction(body, fallbackError) {
        const tr = t();
        const url = buildOrderAmendApiUrl();
        const fallback =
            fallbackError ||
            tr.reportIssueSparePartsError ||
            'We could not load spare parts. Please try again.';
        if (!url) throw new Error(fallback);
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const payload = await res.json().catch(() => ({}));
        if (isAmendPayloadFailed(res, payload)) {
            throw new Error(amendPayloadErrorMessage(payload, fallback));
        }
        return payload;
    }

    async function fetchSparePartsLookup(itemNumber) {
        const cached = readSpareLookupCache('sparePartsLookup', itemNumber);
        if (cached) return cached;
        const payload = await postOrderAmendAction(
            { action: 'sparePartsLookup', itemNumber },
            t().reportIssueSparePartsError
        );
        const parts = extractSparePartsLookup(payload);
        writeSpareLookupCache('sparePartsLookup', itemNumber, parts);
        return parts;
    }

    async function fetchFixingsLookup(itemNumber) {
        const cached = readSpareLookupCache('fixingsLookup', itemNumber);
        if (cached) return cached;
        const fallback = t().reportIssueSparePartsFixingsError;
        let parts;
        const payload = await postOrderAmendAction(
            { action: 'fixingsLookup', itemNumber },
            fallback
        );
        parts = extractFixingsLookup(payload);
        writeSpareLookupCache('fixingsLookup', itemNumber, parts);
        return parts;
    }

    function applyAvailabilityToParts(parts, payload, collectionKey) {
        const byNumber = new Map();
        const byId = new Map();
        unwrapAmendDataList(payload).forEach((assembly) => {
            const list = Array.isArray(assembly?.[collectionKey]) ? assembly[collectionKey] : [];
            list.forEach((part) => {
                const locations = Array.isArray(part?.availability) ? part.availability : [];
                const inStock = locations.some((loc) => Number(loc?.freeStock) > 0);
                const itemNumber = String(part?.itemNumber ?? '').trim();
                const itemId = String(part?.itemId ?? '').trim();
                if (itemNumber) byNumber.set(itemNumber, inStock);
                if (itemId) byId.set(itemId, inStock);
            });
        });
        return (parts || []).map((part) => {
            let inStock;
            if (part.itemNumber && byNumber.has(part.itemNumber)) {
                inStock = byNumber.get(part.itemNumber);
            } else if (part.itemId && byId.has(part.itemId)) {
                inStock = byId.get(part.itemId);
            } else {
                inStock = false;
            }
            return { ...part, stockStatus: inStock ? 'in' : 'out' };
        });
    }

    function markLoadingPartsOutOfStock(parts) {
        return (parts || []).map((part) =>
            part.stockStatus === 'loading' ? { ...part, stockStatus: 'out' } : part
        );
    }

    function openSparePartsView(issueIndex) {
        if (!draft?.issues?.[issueIndex]) return;
        const sameIssue = sparePartsIssueIndex === issueIndex;
        sparePartsIssueIndex = issueIndex;
        if (sameIssue && sparePartsState.componentsLoaded) {
            showSparePartsView();
            return;
        }
        const itemNumber = resolveIssueItemNumber(draft.issues[issueIndex]);
        const cached = readSpareLookupCache('sparePartsLookup', itemNumber);
        sparePartsState = {
            ...createEmptySparePartsState(),
            componentsLoading: !cached,
        };
        restoreSpareQtyFromIssue(draft.issues[issueIndex]);
        showSparePartsView();
        loadSpareParts(issueIndex);
    }

    async function loadSpareParts(issueIndex) {
        const tr = t();
        const issue = draft?.issues?.[issueIndex];
        if (!issue) return;

        const itemNumber = resolveIssueItemNumber(issue);
        const cached = readSpareLookupCache('sparePartsLookup', itemNumber);
        if (cached) {
            sparePartsState.components = cached;
            sparePartsState.componentsLoading = false;
            sparePartsState.componentsLoaded = true;
            sparePartsState.componentsError = '';
            renderSparePartsPanel();
            loadPartsAvailability(issueIndex, 'components');
            return;
        }

        sparePartsState.componentsLoading = true;
        sparePartsState.componentsError = '';
        sparePartsState.componentsLoaded = false;
        renderSparePartsPanel();

        try {
            const components = await fetchSparePartsLookup(itemNumber);
            if (sparePartsIssueIndex !== issueIndex) return;
            sparePartsState.components = components;
            sparePartsState.componentsLoading = false;
            sparePartsState.componentsLoaded = true;
            sparePartsState.componentsError = '';
            renderSparePartsPanel();
            loadPartsAvailability(issueIndex, 'components');
        } catch (err) {
            if (sparePartsIssueIndex !== issueIndex) return;
            console.error(err);
            sparePartsState.componentsLoading = false;
            sparePartsState.componentsLoaded = false;
            sparePartsState.componentsError =
                err?.message ||
                tr.reportIssueSparePartsError ||
                'We could not load spare parts. Please try again.';
            renderSparePartsPanel();
        }
    }

    function refreshSparePartsUi() {
        renderSparePartsPanel();
        if (drawerContext === 'fixings') {
            const drawer = getOrderTrackerDrawer();
            if (drawer?.classList.contains('order-tracker-drawer--open')) {
                refreshFixingsDrawerLive();
            } else {
                renderFixingsDrawer();
            }
        }
        if (view === 'spares') updateHeaderActions();
    }

    async function loadFixings(issueIndex) {
        const tr = t();
        const issue = draft?.issues?.[issueIndex];
        if (!issue) return;

        const itemNumber = resolveIssueItemNumber(issue);
        const cached = readSpareLookupCache('fixingsLookup', itemNumber);
        if (cached) {
            sparePartsState.fixings = cached;
            sparePartsState.fixingsLoading = false;
            sparePartsState.fixingsLoaded = true;
            sparePartsState.fixingsError = '';
            refreshSparePartsUi();
            loadPartsAvailability(issueIndex, 'fixings');
            return;
        }

        sparePartsState.fixingsLoading = true;
        sparePartsState.fixingsError = '';
        sparePartsState.fixingsLoaded = false;
        refreshSparePartsUi();

        try {
            const fixings = await fetchFixingsLookup(itemNumber);
            if (sparePartsIssueIndex !== issueIndex) return;
            sparePartsState.fixings = fixings;
            sparePartsState.fixingsLoading = false;
            sparePartsState.fixingsLoaded = true;
            sparePartsState.fixingsError = '';
            refreshSparePartsUi();
            loadPartsAvailability(issueIndex, 'fixings');
        } catch (err) {
            if (sparePartsIssueIndex !== issueIndex) return;
            console.error(err);
            sparePartsState.fixingsLoading = false;
            sparePartsState.fixingsLoaded = false;
            sparePartsState.fixingsError =
                err?.message ||
                tr.reportIssueSparePartsFixingsError;
            refreshSparePartsUi();
        }
    }

    async function loadPartsAvailability(issueIndex, tab) {
        const isFixings = tab === 'fixings';
        const partsKey = isFixings ? 'fixings' : 'components';
        const collectionKey = isFixings ? 'fixings' : 'spareParts';
        const action = isFixings ? 'fixingsAvailability' : 'sparePartsAvailability';
        const fallback = isFixings
            ? t().reportIssueSparePartsFixingsError
            : t().reportIssueSparePartsError;

        const skus = collectPartSkuNumbers(sparePartsState[partsKey]);
        if (!skus.length) {
            if ((sparePartsState[partsKey] || []).some((part) => part.stockStatus === 'loading')) {
                sparePartsState[partsKey] = markLoadingPartsOutOfStock(sparePartsState[partsKey]);
                refreshSparePartsUi();
            }
            return;
        }
        if (sparePartsIssueIndex !== issueIndex) return;

        try {
            const payload = await postOrderAmendAction(
                { action, itemNumber: skus.join(',') },
                fallback
            );
            if (sparePartsIssueIndex !== issueIndex) return;
            sparePartsState[partsKey] = applyAvailabilityToParts(
                sparePartsState[partsKey],
                payload,
                collectionKey
            );
        } catch (err) {
            if (sparePartsIssueIndex !== issueIndex) return;
            console.error(err);
            sparePartsState[partsKey] = markLoadingPartsOutOfStock(sparePartsState[partsKey]);
        } finally {
            if (sparePartsIssueIndex !== issueIndex) return;
            refreshSparePartsUi();
        }
    }

    function showSparePartsView() {
        hideReportIssueAddressEmbed();
        view = 'spares';
        setVisiblyHidden(getShipmentsContainer(), true);
        setVisiblyHidden(getStepEl(), false);
        if (draft) {
            draft.ui = {
                ...(draft.ui || {}),
                step: 'spares',
                activeIssueIndex: sparePartsIssueIndex,
            };
        }
        updateHeaderActions();
        renderSparePartsPanel();
    }

    function openSubmitAddressView() {
        const snapshot =
            draft?.shippingAddress && typeof draft.shippingAddress === 'object'
                ? draft.shippingAddress
                : null;
        view = 'submit-address';
        setVisiblyHidden(getShipmentsContainer(), true);
        setVisiblyHidden(getStepEl(), false);
        if (draft) {
            draft.ui = {
                ...(draft.ui || {}),
                step: 'submit-address',
            };
        }
        const stepEl = getStepEl();
        const api = global.OrderTrackerAddressUpdate;
        hideReportIssueAddressEmbed();
        if (stepEl) stepEl.innerHTML = '';
        const shown = api?.showAddressPanel?.({
            mode: 'reportIssue',
            snapshot,
            mountEl: stepEl,
        });
        if (!shown) {
            const tr = t();
            panelErrorMessage =
                tr.reportIssueFinaliseError ||
                'Something went wrong submitting your report. Please try again.';
            showDetailsValidationState();
            return;
        }
        updateHeaderActions();
    }

    function backFromSubmitAddressView() {
        persistSubmitAddressSnapshot();
        hideReportIssueAddressEmbed();
        showDetailsView();
    }

    function showSparePartsSummaryView() {
        hideReportIssueAddressEmbed();
        view = 'spares-summary';
        setVisiblyHidden(getShipmentsContainer(), true);
        setVisiblyHidden(getStepEl(), false);
        if (draft) {
            draft.ui = {
                ...(draft.ui || {}),
                step: 'spares-summary',
                activeIssueIndex: sparePartsIssueIndex,
            };
        }
        updateHeaderActions();
        renderSparePartsSummaryPanel();
    }

    function backFromSparePartsSummaryView() {
        showSparePartsView();
    }

    function editSparePartsFromSummary() {
        showSparePartsView();
    }

    function confirmSparePartsSummary() {
        persistSpareQtyToIssue();
        exitSparePartsView({ keepSolution: true });
    }

    function spareSummaryPartTitle(key, kind) {
        const list = kind === 'fixing' ? sparePartsState.fixings : sparePartsState.components;
        const found = (list || []).find((part) => sparePartKey(part) === key);
        if (found?.title) return found.title;
        const snap = sparePartsState.fixingSnapshots?.[key];
        return snap?.title || key;
    }

    function spareSummaryQtyLine(key, qty, kind) {
        const tr = t();
        return replaceTokens(tr.reportIssueSparePartsSummaryQty || '{{ name }} (x{{ qty }})', {
            name: spareSummaryPartTitle(key, kind),
            qty,
        });
    }

    function spareSummaryLinesHtml(qtyMap, kind) {
        const parts = Object.entries(qtyMap || {})
            .map(([key, raw]) => {
                const qty = parseInt(raw, 10) || 0;
                if (qty <= 0 || !key) return '';
                return spareSummaryQtyLine(key, qty, kind);
            })
            .filter(Boolean);
        return parts.join(', ');
    }

    function renderSparePartsSummaryPanel() {
        if (view !== 'spares-summary') return;
        const stepEl = getStepEl();
        const issue = draft?.issues?.[sparePartsIssueIndex];
        if (!stepEl || !issue) return;
        const tr = t();
        const productName = issue.productName || tr.reportIssueSparePartsTitle || 'Spare Parts';
        const componentsLine = spareSummaryLinesHtml(sparePartsState.componentQty, 'component');
        const fixingsLine = spareSummaryLinesHtml(sparePartsState.selectedFixings, 'fixing');

        const componentsBlock = componentsLine
            ? `<div class="order-report-issue__spares-summary-group">
                    <p class="order-report-issue__spares-summary-group-title">${escapeHtml(
                        tr.reportIssueSparePartsSummaryComponents || 'Components'
                    )}</p>
                    <p class="order-report-issue__spares-summary-group-items">${escapeHtml(
                        componentsLine
                    )}</p>
               </div>`
            : '';
        const fixingsBlock = fixingsLine
            ? `<div class="order-report-issue__spares-summary-group">
                    <p class="order-report-issue__spares-summary-group-title">${escapeHtml(
                        tr.reportIssueSparePartsSummaryFixings || 'Fixings'
                    )}</p>
                    <p class="order-report-issue__spares-summary-group-items">${escapeHtml(
                        fixingsLine
                    )}</p>
               </div>`
            : '';

        stepEl.innerHTML = `
            <div class="order-report-issue-spares-summary-root">
                <div class="order-report-issue__spares-summary-card">
                    <div class="order-report-issue__spares-summary-card-head">
                        <p class="order-report-issue__spares-summary-product">${escapeHtml(
                            productName
                        )}</p>
                        <button type="button" class="button order-report-issue__card-edit" data-oa-report-action="spares-summary-edit-parts">
                            <span>${escapeHtml(tr.reportIssueEdit || 'Edit')}</span>
                        </button>
                    </div>
                    <div class="order-report-issue__spares-summary-card-body">
                        ${componentsBlock}
                        ${fixingsBlock}
                    </div>
                </div>
            </div>`;
    }

    function exitSparePartsView({ keepSolution = null } = {}) {
        hideReportIssueAddressEmbed();
        const issue = draft?.issues?.[sparePartsIssueIndex];
        const keep = keepSolution == null ? selectedSpareCount() > 0 : keepSolution;
        if (issue) {
            if (keep && issue.solution) {
                persistSpareQtyToIssue();
            } else {
                issue.solution = null;
                flushPersist();
            }
        }
        sparePartsIssueIndex = null;
        showDetailsView();
    }

    function setSparePartsTab(tab) {
        if (tab !== 'components' && tab !== 'fixings') return;
        const changed = sparePartsState.activeTab !== tab;
        sparePartsState.activeTab = tab;
        if (changed) renderSparePartsPanel();
    }

    function sparePartStockLabel(part) {
        const tr = t();
        if (part.stockStatus === 'in') return tr.reportIssueSparePartsInStock || 'In stock';
        if (part.stockStatus === 'out') return tr.reportIssueSparePartsOutOfStock || 'Out of stock';
        return tr.reportIssueSparePartsLoadingStock || 'loading...';
    }

    function spareTabLabel(kind) {
        const tr = t();
        if (kind === 'fixings') {
            const selected = countQtyMap(sparePartsState.selectedFixings);
            if (!sparePartsState.fixingsLoaded && selected === 0) {
                return tr.reportIssueSparePartsFixingsPending || 'Fixings';
            }
            return replaceTokens(tr.reportIssueSparePartsFixings || 'Fixings ({{ count }})', {
                count: selected,
            });
        }
        if (!sparePartsState.componentsLoaded) {
            return tr.reportIssueSparePartsComponentsPending || 'Components';
        }
        return replaceTokens(tr.reportIssueSparePartsComponents || 'Components ({{ count }})', {
            count: countQtyMap(sparePartsState.componentQty),
        });
    }

    function setSpareQty(scope, key, qty) {
        if (!key) return;
        const part = findSparePart(scope, key);
        const map = qtyMapForScope(scope);
        const current = parseInt(map[key], 10) || 0;
        const max = sparePartMaxQty(part);
        const next = Math.max(0, Math.min(max, parseInt(qty, 10) || 0));
        if (part?.stockStatus !== 'in' && current === 0 && next > 0) return;
        if (next <= 0) delete map[key];
        else map[key] = next;
        if (scope === 'selected' || scope === 'drawer') {
            const snapPart = part || sparePartsState.fixingSnapshots[key];
            if (snapPart) sparePartsState.fixingSnapshots[key] = snapPart;
        }
        if (scope === 'drawer') {
            refreshFixingsDrawerLive();
            return;
        }
        persistSpareQtyToIssue();
        renderSparePartsPanel();
        updateHeaderActions();
    }

    function renderSparePartRowHtml(part, qty, scope) {
        const tr = t();
        const key = sparePartKey(part);
        const max = sparePartMaxQty(part);
        const outOfStock = part.stockStatus === 'out';
        const stockLabel = sparePartStockLabel(part);
        const stockClass = outOfStock ? ' order-report-issue__spares-meta--oos' : '';
        const metaHtml = stockLabel
            ? `<p class="order-report-issue__spares-meta${stockClass}">${escapeHtml(stockLabel)}</p>`
            : '';
        const qtyVal = parseInt(qty, 10) || 0;
        const attrs = `data-oa-report-part-key="${escapeHtmlAttr(key)}" data-oa-report-spares-qty-scope="${escapeHtmlAttr(
            scope
        )}"`;
        let actionHtml = '';
        if (qtyVal > 0) {
            actionHtml = `<div class="order-report-issue__resend-qty">
                    <button type="button" data-oa-report-action="spares-qty-dec" ${attrs} aria-label="Decrease">−</button>
                    <input type="number" class="order-report-issue__resend-qty-value" min="1" max="${max}" value="${qtyVal}" data-oa-report-spares-qty-input ${attrs}>
                    <button type="button" data-oa-report-action="spares-qty-inc" ${attrs}${
                        qtyVal >= max ? ' disabled' : ''
                    } aria-label="Increase">+</button>
                   </div>`;
        } else if (part.stockStatus === 'in') {
            actionHtml = `<button type="button" class="order-report-issue__spares-add" data-oa-report-action="spares-add" ${attrs}>${escapeHtml(
                tr.reportIssueSparePartsAdd || 'Add'
            )}</button>`;
        } else if (part.stockStatus === 'out') {
            actionHtml = `<button type="button" class="order-report-issue__spares-add" data-oa-report-action="spares-add" ${attrs} disabled aria-disabled="true">${escapeHtml(
                tr.reportIssueSparePartsUnavailable || 'Unavailable'
            )}</button>`;
        }

        return `
            <div class="order-report-issue__spares-row">
                <div class="order-report-issue__spares-thumb" aria-hidden="true">${SPARES_THUMB_SVG}</div>
                <div class="order-report-issue__spares-info">
                    <p class="order-report-issue__spares-name">${escapeHtml(part.title || '')}</p>
                    ${metaHtml}
                </div>
                ${actionHtml}
            </div>`;
    }

    function renderComponentsTabHtml() {
        const tr = t();
        if (sparePartsState.componentsLoading) {
            return `<p class="order-report-issue__spares-loading">${escapeHtml(
                tr.reportIssueSparePartsLoading || 'Loading spare parts…'
            )}</p>`;
        }
        if (sparePartsState.componentsError) {
            return `<div class="order-report-issue__panel-error">${validationErrorBannerHtml(
                sparePartsState.componentsError
            )}</div>`;
        }
        const items = sparePartsState.components || [];
        if (items.length) {
            return items
                .map((part) =>
                    renderSparePartRowHtml(
                        part,
                        sparePartsState.componentQty[sparePartKey(part)] || 0,
                        'component'
                    )
                )
                .join('');
        }
        if (sparePartsState.componentsLoaded) {
            return `<p class="order-report-issue__spares-empty">${escapeHtml(
                tr.reportIssueSparePartsEmpty
            )}</p>`;
        }
        return `<p class="order-report-issue__spares-loading">${escapeHtml(
            tr.reportIssueSparePartsLoading || 'Loading spare parts…'
        )}</p>`;
    }

    function renderFixingsTabHtml() {
        const tr = t();
        const selectedKeys = Object.keys(sparePartsState.selectedFixings || {}).filter(
            (key) => (parseInt(sparePartsState.selectedFixings[key], 10) || 0) > 0
        );
        const selectedRows = selectedKeys
            .map((key) => {
                const part = findSparePart('selected', key);
                if (!part) return '';
                return renderSparePartRowHtml(
                    part,
                    sparePartsState.selectedFixings[key],
                    'selected'
                );
            })
            .join('');

        return `
            <div class="order-report-issue__fixings-individual">
                <div class="order-report-issue__fixings-individual-header">
                    <h3 class="order-report-issue__fixings-individual-title">${escapeHtml(
                        tr.reportIssueSparePartsIndividualTitle || 'Individual Fixings'
                    )}</h3>
                    <p class="order-report-issue__fixings-individual-intro">${escapeHtml(
                        tr.reportIssueSparePartsIndividualIntro ||
                            'Select specific fixing parts for your product'
                    )}</p>
                    <button type="button" class="order-report-issue__fixings-add-btn" data-oa-report-action="fixings-open-drawer">
                        ${ADD_FIXINGS_SVG}
                        <span>${escapeHtml(tr.reportIssueSparePartsAddFixings || 'Add Fixings')}</span>
                    </button>
                </div>
                ${
                    selectedRows
                        ? `<div class="order-report-issue__spares-selected">${selectedRows}</div>`
                        : '<p class="order-report-issue__spares-empty">(list empty)</p>'
                }
            </div>`;
    }

    function renderSpareTabListHtml(activeTab) {
        if (activeTab === 'fixings') return renderFixingsTabHtml();
        return renderComponentsTabHtml();
    }

    function filteredFixings() {
        const q = String(sparePartsState.fixingsSearch || '')
            .trim()
            .toLowerCase();
        const items = sparePartsState.fixings || [];
        if (!q) return items;
        return items.filter((part) => {
            const title = String(part.title || '').toLowerCase();
            const number = String(part.itemNumber || '').toLowerCase();
            return title.includes(q) || number.includes(q);
        });
    }

    function renderFixingsDrawerListHtml() {
        const tr = t();
        if (sparePartsState.fixingsLoading) {
            return `<p class="order-report-issue__spares-loading">${escapeHtml(
                tr.reportIssueSparePartsFixingsLoading || 'Loading fixings…'
            )}</p>`;
        }
        if (sparePartsState.fixingsError) {
            return `<div class="order-report-issue__panel-error">${validationErrorBannerHtml(
                sparePartsState.fixingsError
            )}</div>`;
        }
        const items = filteredFixings();
        if (items.length) {
            return items
                .map((part) =>
                    renderSparePartRowHtml(
                        part,
                        sparePartsState.drawerFixingQty[sparePartKey(part)] || 0,
                        'drawer'
                    )
                )
                .join('');
        }
        if (sparePartsState.fixingsLoaded) {
            return `<p class="order-report-issue__spares-empty">${escapeHtml(
                tr.reportIssueSparePartsEmpty
            )}</p>`;
        }
        return `<p class="order-report-issue__spares-loading">${escapeHtml(
            tr.reportIssueSparePartsFixingsLoading || 'Loading fixings…'
        )}</p>`;
    }

    function renderFixingsDrawer() {
        const tr = t();
        const drawer = getOrderTrackerDrawer();
        if (!drawer) return;
        const count = countQtyMap(sparePartsState.drawerFixingQty);
        const searchValue = sparePartsState.fixingsSearch || '';
        const searchPlaceholder = tr.reportIssueSparePartsSearch || 'Search';
        const html = `
            <div class="order-report-issue__fixings-drawer">
                <div class="order-report-issue__fixings-drawer-top">
                    <button type="button" class="order-report-issue__fixings-drawer-close" data-oa-report-action="fixings-drawer-back" aria-label="${escapeHtmlAttr(
                        tr.reportIssueClose || 'Close'
                    )}">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
                    </button>
                    <h2 class="order-report-issue__fixings-drawer-title">${escapeHtml(
                        tr.reportIssueSparePartsIndividualTitle || 'Individual Fixings'
                    )}</h2>
                    <p class="order-report-issue__fixings-drawer-intro">${escapeHtml(
                        tr.reportIssueSparePartsIndividualIntro ||
                            'Select specific fixing parts for your product'
                    )}</p>
                    <label class="order-report-issue__fixings-search">
                        <span class="visually-hidden">${escapeHtml(searchPlaceholder)}</span>
                        <input type="search" data-oa-report-fixings-search value="${escapeHtmlAttr(
                            searchValue
                        )}" placeholder="${escapeHtmlAttr(searchPlaceholder)}">
                        <button type="button" class="order-report-issue__fixings-search-clear" data-oa-report-action="fixings-search-clear" aria-label="${escapeHtmlAttr(
                            tr.reportIssueClose || 'Close'
                        )}"${searchValue ? '' : ' hidden'}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                        </button>
                    </label>
                </div>
                <div class="order-report-issue__fixings-drawer-list">
                    ${renderFixingsDrawerListHtml()}
                </div>
            </div>`;
        const footerHtml = `
            <div class="order-report-issue__fixings-drawer-actions">
                <button type="button" class="button button--primary" data-oa-report-action="fixings-drawer-add">${escapeHtml(
                    replaceTokens(tr.reportIssueSparePartsAddCount || 'Add ({{ count }})', { count })
                )}</button>
                <button type="button" class="button button--secondary" data-oa-report-action="fixings-drawer-back">${escapeHtml(
                    tr.reportIssueClose || 'Close'
                )}</button>
            </div>`;
        drawer.classList.add('order-tracker-drawer--fixings');
        if (drawer.classList.contains('order-tracker-drawer--open')) {
            if (drawer.contentEl) drawer.contentEl.innerHTML = html;
            drawer.footerHtml(footerHtml);
            return;
        }
        drawer.open({ title: '', html, footerHtml });
    }

    function refreshFixingsDrawerLive() {
        const drawer = getOrderTrackerDrawer();
        if (!drawer || drawerContext !== 'fixings') return;
        const list = drawer.querySelector('.order-report-issue__fixings-drawer-list');
        const addBtn = drawer.querySelector('[data-oa-report-action="fixings-drawer-add"]');
        if (list) list.innerHTML = renderFixingsDrawerListHtml();
        if (addBtn) {
            const tr = t();
            addBtn.textContent = replaceTokens(tr.reportIssueSparePartsAddCount || 'Add ({{ count }})', {
                count: countQtyMap(sparePartsState.drawerFixingQty),
            });
        }
    }

    function openFixingsDrawer() {
        sparePartsState.drawerFixingQty = { ...sparePartsState.selectedFixings };
        sparePartsState.fixingsSearch = '';
        drawerContext = 'fixings';
        renderFixingsDrawer();
        if (!sparePartsState.fixingsLoaded && !sparePartsState.fixingsLoading) {
            loadFixings(sparePartsIssueIndex);
        }
    }

    function closeFixingsDrawer({ commit = false } = {}) {
        if (commit) {
            const next = {};
            Object.entries(sparePartsState.drawerFixingQty || {}).forEach(([key, qty]) => {
                const n = parseInt(qty, 10) || 0;
                if (n > 0) next[key] = n;
            });
            sparePartsState.selectedFixings = next;
            snapshotSelectedFixings();
            persistSpareQtyToIssue();
        }
        sparePartsState.drawerFixingQty = {};
        sparePartsState.fixingsSearch = '';
        const drawer = getOrderTrackerDrawer();
        drawerContext = null;
        drawer?.classList.remove('order-tracker-drawer--fixings');
        if (drawer?.classList.contains('order-tracker-drawer--open')) {
            drawer.close();
        }
        renderSparePartsPanel();
        updateHeaderActions();
    }

    function renderSparePartsPanel() {
        if (view !== 'spares') return;
        const stepEl = getStepEl();
        if (!stepEl) return;

        const activeTab = sparePartsState.activeTab === 'fixings' ? 'fixings' : 'components';
        const componentsLabel = spareTabLabel('components');
        const fixingsLabel = spareTabLabel('fixings');

        stepEl.innerHTML = `
            <div class="order-report-issue-spares-root">
                <div class="order-report-issue__spares-panel">
                    <div class="order-report-issue__spares-tabs" role="tablist">
                        <button type="button" class="order-report-issue__spares-tab${
                            activeTab === 'components' ? ' order-report-issue__spares-tab--active' : ''
                        }" role="tab" aria-selected="${activeTab === 'components'}" data-oa-report-action="spares-tab" data-oa-report-spares-tab="components">${escapeHtml(
                            componentsLabel
                        )}</button>
                        <button type="button" class="order-report-issue__spares-tab${
                            activeTab === 'fixings' ? ' order-report-issue__spares-tab--active' : ''
                        }" role="tab" aria-selected="${activeTab === 'fixings'}" data-oa-report-action="spares-tab" data-oa-report-spares-tab="fixings">${escapeHtml(
                            fixingsLabel
                        )}</button>
                    </div>
                    <div class="order-report-issue__spares-list" role="tabpanel">
                        ${renderSpareTabListHtml(activeTab)}
                    </div>
                </div>
            </div>`;
    }

    /* --- Media upload --- */

    function getClientApiKey() {
        return document.querySelector('#apiKeyContainer')?.dataset.apiKey || '';
    }

    function getUploadUrlBase() {
        return (getRoot()?.dataset.reportIssueUploadUrl || '').trim();
    }

    function buildUploadUrl() {
        const base = getUploadUrlBase();
        const apiKey = getClientApiKey();
        if (!base || !apiKey) return '';
        try {
            const url = new URL(base);
            url.searchParams.set('clientApiKey', apiKey);
            return url.toString();
        } catch {
            return '';
        }
    }

    function fileExtension(file) {
        const name = String(file?.name || '');
        const dot = name.lastIndexOf('.');
        if (dot < 0 || dot === name.length - 1) return '';
        return name.slice(dot + 1).toLowerCase();
    }

    function matchAllowedUploadType(file) {
        const ext = fileExtension(file);
        const mime = String(file?.type || '').toLowerCase();
        return (
            ALLOWED_UPLOAD_TYPES.find(
                (entry) =>
                    (ext && entry.ext.includes(ext)) || (mime && entry.mime.includes(mime))
            ) || null
        );
    }

    function allowedFormatList() {
        const exts = [];
        ALLOWED_UPLOAD_TYPES.forEach((entry) => {
            entry.ext.forEach((ext) => exts.push(`.${ext}`));
        });
        return exts.join(', ');
    }

    function uploadAcceptAttribute() {
        const tokens = [];
        ALLOWED_UPLOAD_TYPES.forEach((entry) => {
            entry.ext.forEach((ext) => tokens.push(`.${ext}`));
            entry.mime.forEach((mime) => tokens.push(mime));
        });
        return tokens.join(',');
    }

    function detectMediaType(file) {
        return matchAllowedUploadType(file)?.kind || null;
    }

    function mediaThumbSrc(media) {
        return (media?.thumbnailUrl || media?.url || '').trim();
    }

    function validateFileForUpload(file) {
        const tr = t();
        const match = matchAllowedUploadType(file);
        if (!match) {
            throw new Error(
                replaceTokens(
                    tr.reportIssueUploadErrorType ||
                        'Unrecognized file format. Supported file formats include {{ formats }}',
                    { formats: allowedFormatList() }
                )
            );
        }
        if (file.size > MAX_FILE_BYTES) {
            throw new Error(
                tr.reportIssueUploadErrorSizeImage ||
                    tr.reportIssueUploadErrorSizeVideo ||
                    'The file size exceeds the 50mb size limit'
            );
        }
        return match.kind;
    }

    function mapUploadedFileItem(item, fallbackType) {
        const mediaType = String(item?.mediaType || fallbackType || 'image').toLowerCase();
        const type = mediaType.startsWith('video') ? 'video' : 'image';
        const sizeBytes = Number(item?.sizeBytes);
        return {
            id: item?.mediaId || generateId('media'),
            mediaId: item?.mediaId || '',
            url: item?.url || '',
            thumbnailUrl: item?.thumbnailUrl || '',
            fileName: item?.fileName || '',
            mediaType: type,
            contentType: item?.contentType || '',
            sizeBytes: Number.isFinite(sizeBytes) ? sizeBytes : 0,
            description: '',
            type,
        };
    }

    async function uploadMediaFiles(files) {
        const tr = t();
        const uploadUrl = buildUploadUrl();
        if (!uploadUrl) {
            throw new Error(
                tr.reportIssueUploadErrorNotConfigured ||
                    'Media upload is not configured. Please try again later.'
            );
        }

        const form = new FormData();
        files.forEach((file) => {
            form.append('files', file);
        });

        const res = await fetch(uploadUrl, {
            method: 'POST',
            body: form
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(
                data.errorMessage || data.message || tr.reportIssueUploadErrorFailed || 'Upload failed'
            );
        }

        const items = Array.isArray(data?.files) ? data.files : [];
        if (!items.length) {
            throw new Error(tr.reportIssueUploadErrorFailed || 'Upload failed');
        }

        return items.map((item, index) => {
            const fallbackType = detectMediaType(files[index]) || 'image';
            return mapUploadedFileItem(item, fallbackType);
        });
    }

    function setUploadBusy(busy) {
        uploadInFlight = Boolean(busy);
        const drawer = getOrderTrackerDrawer();
        drawer?.setCloseBlocked?.(uploadInFlight);

        const area = drawer?.querySelector('.order-report-issue__upload-area');
        const input = drawer?.querySelector('[data-oa-report-file]');
        const label = area?.querySelector('span:last-child');
        if (area) area.classList.toggle('order-report-issue__upload-area--busy', uploadInFlight);
        if (input) input.disabled = uploadInFlight;
        if (label) {
            const tr = t();
            label.textContent = uploadInFlight
                ? tr.reportIssueUploadUploading || 'Uploading…'
                : tr.reportIssueUploadBtn || 'Upload Image(s)';
        }

        const footerActions = [
            'drawer-next',
            'drawer-back',
            'drawer-close',
            'drawer-finish',
        ];
        footerActions.forEach((action) => {
            drawer
                ?.querySelectorAll(`[data-oa-report-action="${action}"]`)
                .forEach((btn) => {
                    btn.disabled = uploadInFlight;
                });
        });
    }

    async function handleFilesSelected(fileList) {
        const tr = t();
        if (!pendingIssue || uploadInFlight) return;
        const files = Array.from(fileList || []);
        if (!files.length) return;

        const remaining = MAX_MEDIA - (pendingIssue.media?.length || 0);
        if (remaining <= 0 || files.length > remaining) {
            showDrawerError(tr.reportIssueUploadErrorCount || 'Max 10 files');
            return;
        }

        const batch = files.slice(0, remaining);
        showDrawerError('');

        try {
            batch.forEach((file) => validateFileForUpload(file));
        } catch (err) {
            showDrawerError(err?.message || tr.reportIssueUploadErrorFailed || 'Upload failed');
            return;
        }

        setUploadBusy(true);
        try {
            const uploaded = await uploadMediaFiles(batch);
            pendingIssue.media = pendingIssue.media || [];
            pendingIssue.media.push(...uploaded);
            openDrawerStep('uploads');
        } catch (err) {
            showDrawerError(err?.message || tr.reportIssueUploadErrorFailed || 'Upload failed');
        } finally {
            setUploadBusy(false);
        }
    }

    function removeMediaAt(index) {
        if (!pendingIssue?.media) return;
        pendingIssue.media.splice(index, 1);
        if (drawerStep === 'uploads' || drawerStep === 'comments' || drawerStep === 'solution' || drawerStep === 'review') {
            openDrawerStep(drawerStep);
        }
    }

    /* --- Report Details panel --- */

    function renderIssueCardHtml(issue, index) {
        const tr = t();
        const img =
            issue.imageUrl && String(issue.imageUrl).trim()
                ? `<img src="${escapeHtmlAttr(issue.imageUrl)}" alt="" class="order-report-issue__card-image">`
                : `<div class="order-report-issue__card-image order-report-issue__card-image--placeholder"></div>`;

        const thumbs = (issue.media || [])
            .map(
                (m, mi) => `
            <div class="order-report-issue__thumb">
                ${
                    m.type === 'video'
                        ? `<span class="order-report-issue__thumb-video">▶</span>`
                        : `<img src="${escapeHtmlAttr(mediaThumbSrc(m))}" alt="">`
                }
                <button type="button" class="order-report-issue__thumb-remove" data-oa-report-action="remove-card-media" data-oa-report-issue-index="${index}" data-media-index="${mi}" aria-label="Remove">&times;</button>
            </div>`
            )
            .join('');

        const solutionHtml = issue.solution?.type
            ? `
                <div class="order-report-issue__card-solution">
                    <span>${escapeHtml(
                        replaceTokens(tr.reportIssueSolutionLine || 'Solution: {{ solution }}', {
                            solution: labelForSolutionType(issue.solution.type),
                        })
                    )}</span>
                    ${
                        SHOW_CHOOSE_SOLUTION
                            ? `<button type="button" class="button order-report-issue__choose-solution" data-oa-report-action="choose-solution" data-oa-report-issue-index="${index}">
                        ${CHANGE_ICON_SVG}
                        <span>${escapeHtml(tr.reportIssueChange)}</span>
                    </button>`
                            : ''
                    }
                </div>`
            : `
                <div class="order-report-issue__card-solution">
                    <span>${escapeHtml(tr.reportIssueNoSolution || 'No solution selected')}</span>
                    ${
                        SHOW_CHOOSE_SOLUTION
                            ? `<button type="button" class="button order-report-issue__choose-solution test3" data-oa-report-action="choose-solution" data-oa-report-issue-index="${index}">${escapeHtml(
                        tr.reportIssueChooseSolution || 'Choose'
                    )}</button>`
                            : ''
                    }
                </div>`;

        const cardError = validationErrorBannerHtml(validationErrorsByIndex[index] || '');

        return `
            <div class="order-report-issue__card${
                validationErrorsByIndex[index] ? ' order-report-issue__card--invalid' : ''
            }" data-oa-report-issue-index="${index}">
                <div class="order-report-issue__card-main">
                    ${img}
                    <div class="order-report-issue__card-info">
                        <p class="order-report-issue__card-name">${escapeHtml(issue.productName || '')}</p>
                        <p class="order-report-issue__card-meta">${escapeHtml(
                            replaceTokens(tr.reportIssueQuantityLine || 'Quantity: {{ qty }}', {
                                qty: issue.reportedQty,
                            })
                        )}</p>
                        <p class="order-report-issue__card-meta">${
                            replaceTokens(tr.reportIssueIssueLine || 'Issue: {{ issue }}', {
                                issue: labelForIssueType(issue.issueType),
                            })
                        }</p>
                    </div>
                    <div class="order-report-issue__card-actions">
                        <button type="button" class="order-report-issue__card-remove" data-oa-report-action="remove-issue" data-oa-report-issue-index="${index}" aria-label="Remove">&times;</button>
                        <button type="button" class="button order-report-issue__card-edit" data-oa-report-action="edit-issue" data-oa-report-issue-index="${index}">
                                ${EDIT_ICON_SVG}
                                <span>${escapeHtml(tr.reportIssueEdit)}</span>
                            </button>
                    </div>
                </div>
                ${
                    thumbs
                        ? `<div class="order-report-issue__card-uploads"><p>${escapeHtml(
                              tr.reportIssueUploadsSummary || 'Uploads'
                          )}</p><div class="order-report-issue__thumbs">${thumbs}</div></div>`
                        : ''
                }
                ${
                    issue.solution?.type === 'contact_support' && String(issue.description || '').trim()
                        ? `<div class="order-report-issue__card-comments">
                            <p>${escapeHtml(commentsLabelForIssueType(issue.issueType))}</p>
                            <p class="order-report-issue__card-comments-text">${escapeHtml(
                                issue.description
                            )}</p>
                           </div>`
                        : ''
                }
                ${solutionHtml}
                ${cardError}
            </div>`;
    }

    function renderReportDetailsPanel() {
        const tr = t();
        const stepEl = getStepEl();
        if (!stepEl || !draft) return;

        const cards = (draft.issues || []).map((issue, i) => renderIssueCardHtml(issue, i)).join('');
        const panelError = panelErrorMessage
            ? `<div class="order-report-issue__panel-error">${validationErrorBannerHtml(
                  panelErrorMessage
              )}</div>`
            : '';

        stepEl.innerHTML = `
            <div class="order-report-issue-details-root">
                ${panelError}
                <div class="order-report-issue__cards">${cards}</div>
                <button type="button" class="order-report-issue__report-another" data-oa-report-action="report-another">${escapeHtml(
                    tr.reportIssueReportAnother || 'Report another issue +'
                )}</button>
            </div>`;
    }

    function firstNonEmptyString(...values) {
        for (const value of values) {
            if (value == null) continue;
            const str = String(value).trim();
            if (str) return str;
        }
        return '';
    }

    function pickResponseField(data, keys) {
        if (!data || typeof data !== 'object') return '';
        const nested = [data, data.data, data.result, data.payload];
        for (const obj of nested) {
            if (!obj || typeof obj !== 'object') continue;
            for (const key of keys) {
                const found = firstNonEmptyString(obj[key]);
                if (found) return found;
            }
        }
        return '';
    }

    function extractTicketId(data) {
        return pickResponseField(data, ['ticketId', 'ticketID', 'ticket', 'caseNumber']);
    }

    function responseDataBody(response) {
        if (!response || typeof response !== 'object' || response instanceof Error) return null;
        const inner = response.data;
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) return inner;
        return response;
    }

    function uniqueResponseValues(list, field) {
        const seen = new Set();
        const values = [];
        (Array.isArray(list) ? list : []).forEach((row) => {
            const value = firstNonEmptyString(row?.[field]);
            if (!value || seen.has(value)) return;
            seen.add(value);
            values.push(value);
        });
        return values;
    }

    function extractSalesOrderNumbers(response) {
        const body = responseDataBody(response);
        return uniqueResponseValues(body?.salesOrders, 'salesOrderNumber');
    }

    function extractReturnAuthNumbers(response) {
        const body = responseDataBody(response);
        const fromReturns = uniqueResponseValues(body?.returns, 'returnAuthNumber');
        if (fromReturns.length) return fromReturns;
        const legacy = pickResponseField(response, ['rma', 'rmaNumber', 'RMA']);
        return legacy ? [legacy] : [];
    }

    function isSaveSolutionOk(res) {
        if (!res || typeof res !== 'object' || res instanceof Error) return false;
        if (res.success === false) return false;
        const inner = res.data;
        if (inner && typeof inner === 'object' && !Array.isArray(inner) && inner.success === false) {
            return false;
        }
        return true;
    }

    function responseErrorMessage(res, fallback) {
        if (res instanceof Error) return res.message || fallback;
        return res?.errorMessage || res?.message || fallback;
    }

    function pickTicketAndRma(response) {
        if (!response || response instanceof Error) {
            return { ticketId: '', rmaNumbers: [], salesOrderNumbers: [] };
        }
        return {
            ticketId: extractTicketId(response),
            rmaNumbers: extractReturnAuthNumbers(response),
            salesOrderNumbers: extractSalesOrderNumbers(response),
        };
    }

    function successQtyBadgeHtml(qty) {
        const tr = t();
        return `<span class="order-report-issue__success-qty">${escapeHtml(
            replaceTokens(tr.reportIssueSuccessQtyBadge || 'Qty {{ qty }}', { qty })
        )}</span>`;
    }

    function successProductRowHtml(issue) {
        const qty = parseInt(issue?.reportedQty, 10) || 0;
        return `
            <div class="order-report-issue__success-product">
                <span class="order-report-issue__success-product-name">${escapeHtml(
                    issue?.productName || ''
                )}</span>
                ${successQtyBadgeHtml(qty)}
            </div>`;
    }

    function renderSuccessPanel(snapshot = {}) {    
        const tr = t();
        const stepEl = getStepEl();
        if (!stepEl) return;
        hideReportIssueAddressEmbed();
        view = 'success';
        setVisiblyHidden(document.querySelector('.order-header'), true);
        updateHeaderActions();

        const submittedIssues = Array.isArray(snapshot.issues) ? snapshot.issues : [];
        const response = snapshot.response;
        const submitOk = Boolean(snapshot.ok);
        const { ticketId, rmaNumbers, salesOrderNumbers } = pickTicketAndRma(
            submitOk ? response : null
        );
        const replacementCardTitle = (index) => {
            if (salesOrderNumbers.length === 1) return salesOrderNumbers[0];
            return (
                salesOrderNumbers[index] ||
                tr.reportIssueSuccessReplacementTitle ||
                'Replacement'
            );
        };
        // The batch either lands or it doesn't — there is no partial success to report per issue.
        const okIssues = submitOk ? submittedIssues : [];
        const allFailed = !submitOk;
        const successTitle = allFailed
            ? tr.reportIssueFinaliseFailTitle || 'Request Failed'
            : tr.reportIssueFinaliseSuccessTitle || 'Request Confirmed';
        const supportIssues = okIssues.filter((issue) => issue?.solution?.type === 'contact_support');
        const replacementIssues = okIssues.filter((issue) => issue?.solution?.type === 'replacement');
        const returnIssues = replacementIssues.filter(
            (issue) => issue?.solution?.disposition === 'return'
        );

        const ticketHtml = ticketId
            ? `<p class="order-report-issue__success-ticket">${escapeHtml(
                  replaceTokens(tr.reportIssueSuccessTicket || 'Ticket #{{ id }}', { id: ticketId })
              )}</p>`
            : '';

        const supportHtml = supportIssues.length
            ? `<section class="order-report-issue__success-section">
                    <h3 class="order-report-issue__success-heading">${escapeHtml(
                        tr.reportIssueSuccessSupportHeading || 'Support request'
                    )}</h3>
                    <div class="order-report-issue__success-card">
                        <p>${escapeHtml(
                            tr.reportIssueSuccessSupportMessage ||
                                'Your request has been submitted to our Customer Support team and someone should be in touch shortly to assist you with your enquiry.'
                        )}</p>
                    </div>
               </section>`
            : '';

        const rmaHtml = rmaNumbers
            .map(
                (rma) => `<div class="order-report-issue__success-rma">${escapeHtml(rma)}</div>`
            )
            .join('');
        const returnHtml = returnIssues.length
            ? `<section class="order-report-issue__success-section">
                    <h3 class="order-report-issue__success-heading">${escapeHtml(
                        tr.reportIssueSuccessReturnHeading || 'Return request'
                    )}</h3>
                    <div class="order-report-issue__success-card">
                        ${returnIssues.map(successProductRowHtml).join('')}
                        <p class="order-report-issue__success-return-intro">${escapeHtml(
                            tr.reportIssueSuccessReturnIntro ||
                                'To proceed with your return, please clearly write the following RMA number on all outer cartons:'
                        )}</p>
                        ${rmaHtml}
                    </div>
               </section>`
            : '';

        const ordersHtml = replacementIssues.length
            ? `<section class="order-report-issue__success-section">
                    <h3 class="order-report-issue__success-heading">${escapeHtml(
                        tr.reportIssueSuccessOrdersHeading || 'Orders'
                    )}</h3>
                    ${replacementIssues
                        .map(
                            (issue, index) => `
                        <div class="order-report-issue__success-order-card">
                            <div class="order-report-issue__success-order-head">
                                <span>${escapeHtml(replacementCardTitle(index))}</span>
                                <span class="order-report-issue__success-status">${CLOCK_ICON_SVG}<span>${escapeHtml(
                                    tr.reportIssueSuccessApprovalPending || 'Approval Pending'
                                )}</span></span>
                            </div>
                            <div class="order-report-issue__success-order-body">
                                ${successProductRowHtml(issue)}
                            </div>
                        </div>`
                        )
                        .join('')}
               </section>`
            : '';

        const errorsHtml = allFailed
            ? `<section class="order-report-issue__success-section">
                    <h3 class="order-report-issue__success-heading">${escapeHtml(
                        tr.reportIssueSuccessErrorsHeading || 'Could not submit'
                    )}</h3>
                    <div class="order-report-issue__success-card order-report-issue__success-card--errors">
                        <div class="order-report-issue__success-error-row">
                            <p class="order-report-issue__success-error-msg">${escapeHtml(
                                responseErrorMessage(
                                    response,
                                    tr.reportIssueFinaliseError ||
                                        'Something went wrong submitting your report. Please try again.'
                                )
                            )}</p>
                        </div>
                    </div>
               </section>`
            : '';

        stepEl.innerHTML = `
            <div class="order-report-issue-details-root order-report-issue-details-root--success">
                <header class="order-report-issue__success-header">
                    <h2 class="order-report-issue__success-title">${escapeHtml(successTitle)}</h2>
                    ${ticketHtml}
                </header>
                ${supportHtml}
                ${returnHtml}
                ${ordersHtml}
                ${errorsHtml}
                <button type="button" class="button button--primary order-report-issue__success-finish" data-oa-report-action="success-done">${escapeHtml(
                    tr.reportIssueFinaliseDone || 'Finish'
                )}</button>
            </div>`;
        setVisiblyHidden(getShipmentsContainer(), true);
        setVisiblyHidden(stepEl, false);
    }

    /* --- Finalise --- */

    function getFinaliseUrlBase() {
        const fromDom = (getRoot()?.dataset.orderAmendApiUrl || '').trim();
        return fromDom || DEFAULT_FINALISE_URL;
    }

    function buildFinaliseUrl() {
        const base = getFinaliseUrlBase();
        const apiKey = getClientApiKey();
        if (!base || !apiKey) return '';
        try {
            const url = new URL(base);
            url.searchParams.set('clientApiKey', apiKey);
            return url.toString();
        } catch {
            return '';
        }
    }

    function setFinaliseBusy(busy) {
        finaliseInFlight = busy;
        const btn = document.getElementById('order-report-issue-finalise');
        if (!btn) return;
        const tr = t();
        btn.disabled = busy || !(draft?.issues?.length > 0);
        if (busy) {
            btn.dataset.oaReportFinaliseLabel = btn.textContent || '';
            btn.textContent = tr.reportIssueFinaliseSubmitting || 'Submitting…';
        } else if (btn.dataset.oaReportFinaliseLabel) {
            btn.textContent = btn.dataset.oaReportFinaliseLabel;
            delete btn.dataset.oaReportFinaliseLabel;
        }
    }

    function toApiLineId(raw) {
        const s = String(raw ?? '').trim();
        if (!s) return '';
        return s.slice(s.lastIndexOf('-') + 1);
    }

    function validateIssuesForFinalise() {
        const tr = t();
        const issues = draft?.issues || [];
        /** @type {Record<number, string>} */
        const errorsByIndex = {};

        if (!issues.length) {
            return {
                ok: false,
                errorsByIndex,
                panelMessage:
                    tr.reportIssueFinaliseIncomplete ||
                    'Please complete this reported issue before finalising.',
            };
        }

        issues.forEach((issue, index) => {
            const qty = parseInt(issue.reportedQty, 10);
            if (!issue.issueType || !Number.isFinite(qty) || qty < 1) {
                errorsByIndex[index] =
                    tr.reportIssueFinaliseIncomplete ||
                    'Please complete this reported issue before finalising.';
                return;
            }
            if (!(issue.media || []).length && !uploadsOptionalForIssueType(issue.issueType)) {
                errorsByIndex[index] =
                    tr.reportIssueFinaliseUploadsRequired || 'At least one upload is required.';
                return;
            }
            const solution = issue.solution;
            const hasSolution = Boolean(solution?.type) || solution?.typeID != null;
            const replacementIncomplete =
                solution?.type === 'replacement' && !(solution.resendItems || []).length;
            const spareIncomplete =
                solution?.type === 'spare_parts' &&
                !buildSpareItemsPayload(solution.componentQty).length &&
                !buildSpareItemsPayload(solution.selectedFixings).length;
            if (!hasSolution || replacementIncomplete || spareIncomplete) {
                errorsByIndex[index] =
                    tr.reportIssueFinaliseSolutionRequired || 'Please choose a solution.';
            }
        });

        return {
            ok: Object.keys(errorsByIndex).length === 0,
            errorsByIndex,
            panelMessage: '',
        };
    }

    function buildResendItemsPayload(issue) {
        const resendItems = Array.isArray(issue.solution?.resendItems) ? issue.solution.resendItems : [];
        return resendItems.map((ri) => {
            const unitPrice = parseNumber(ri.unitPrice);
            const item = {
                itemNumber: String(ri.itemNumber || ''),
                quantity: parseInt(ri.quantity, 10) || 0,
                rate: unitPrice,
                unitPrice,
            };
            
            if ('earliestDeliveryDate' in ri || 'latestDeliveryDate' in ri) {
                item.earliestDeliveryDate = ri.earliestDeliveryDate || null;
                item.latestDeliveryDate = ri.latestDeliveryDate || null;
            }
            return item;
        });
    }

    function buildSpareItemsPayload(qtyMap) {
        return Object.entries(qtyMap || {})
            .map(([itemNumber, raw]) => {
                const quantity = parseInt(raw, 10) || 0;
                const sku = String(itemNumber || '').trim();
                if (!sku || quantity <= 0) return null;
                return {
                    itemNumber: sku,
                    quantity,
                    rate: 0,
                    unitPrice: 0,
                };
            })
            .filter(Boolean);
    }

    function buildSparePayload(issue) {
        return {
            spareParts: buildSpareItemsPayload(issue?.solution?.componentQty),
            fixings: buildSpareItemsPayload(issue?.solution?.selectedFixings),
        };
    }

    function reportNeedsShippingAddress() {
        return (draft?.issues || []).some((issue) => {
            const type = issue?.solution?.type;
            return type === 'replacement' || type === 'spare_parts';
        });
    }

    function buildReturnCollectionPayload(issue, entry, qty) {
        const unitPrice = parseNumber(entry?.item?.unitPrice);
        return {
            items: [
                {
                    itemNumber: String(issue.itemID || ''),
                    quantity: qty,
                    rate: unitPrice,
                    unitPrice,
                },
            ],
            returnOption: { id: 1 },
        };
    }

    function buildSolutionDetailPayload(issue, entry, qty) {
        const detail = { syncToWMS: false };

        if (issue.solution?.type === 'contact_support') {
            detail.supportContact = true;
            return detail;
        }

        if (issue.solution?.type === 'spare_parts') {
            detail.spare = buildSparePayload(issue);
            detail.supportContact = false;
            return detail;
        }

        const resendItems = buildResendItemsPayload(issue);
        if (resendItems.length) detail.resend = { items: resendItems };
        // returnCollection is sent only when the customer returns the original; "keep" omits it.
        if (issue.solution?.disposition === 'return') {
            detail.returnCollection = buildReturnCollectionPayload(issue, entry, qty);
        }
        if (dispositionHiddenForIssueType(issue.issueType)) {
            detail.originalItem = '';
        } else if (issue.solution?.disposition) {
            detail.originalItem = issue.solution.disposition;
        }
        detail.supportContact = false;
        return detail;
    }

    function buildSubmitSolutionItemPayload(issue) {
        const entry = itemRegistry.get(issue.lineKey);
        const item = entry?.item;
        const qty = parseInt(issue.reportedQty, 10) || 0;
        return {
            lineId: toApiLineId(item?.id || issue.originalSOLineID),
            itemNumber: String(issue.itemID || item?.itemNumber || ''),
            quantity: qty,
            reportedIssue: reportedIssuePayload(issue.issueType),
            reportingCode: String(issue.reportingCode || item?.reportingCode || ''),
            additionalComments: String(issue.description || ''),
            files: (issue.media || []).map((m) => ({
                mediaId: String(m.mediaId || m.id || ''),
                fileName: String(m.fileName || ''),
                mediaType: String(m.mediaType || m.type || 'image'),
                contentType: String(m.contentType || ''),
                sizeBytes: Number(m.sizeBytes) || 0,
                url: String(m.url || ''),
                thumbnailUrl: String(m.thumbnailUrl || ''),
            })),
            solutionDetail: buildSolutionDetailPayload(issue, entry, qty),
        };
    }

    function buildSubmitSolutionsPayload() {
        const order = getOrderContext();
        const payload = {
            action: 'submitOrderStatusSolutions',
            platformOrderNum: String(getOrderNumber()),
            salesOrderId: String(order?.orderSummary?.orderInternalId ?? ''),
            customerEmail: getCustomerEmail(),
            items: (draft?.issues || []).map(buildSubmitSolutionItemPayload),
        };
        if (reportNeedsShippingAddress()) {
            const shipTo = global.OrderTrackerAddressUpdate?.getShipToPayload?.();
            if (shipTo) payload.shippingAddress = { shipTo };
        }
        return payload;
    }

    async function postSubmitSolutions(url, payload) {
        const tr = t();
        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                return new Error(
                    responseErrorMessage(
                        data,
                        tr.reportIssueFinaliseError ||
                            'Something went wrong submitting your report. Please try again.'
                    )
                );
            }
            return data;
        } catch (err) {
            return err;
        }
    }

    function showDetailsValidationState() {
        hideReportIssueAddressEmbed();
        if (view !== 'details' && !(draft?.issues?.length > 0)) {
            showDetailsView();
            return;
        }
        view = 'details';
        renderReportDetailsPanel();
        setVisiblyHidden(getShipmentsContainer(), true);
        setVisiblyHidden(getStepEl(), false);
        updateHeaderActions();
    }

    async function finaliseReport() {
        if (finaliseInFlight || !draft?.issues?.length) return;

        const tr = t();
        clearValidationErrors();
        const validation = validateIssuesForFinalise();
        if (!validation.ok) {
            validationErrorsByIndex = validation.errorsByIndex || {};
            panelErrorMessage = validation.panelMessage || '';
            showDetailsValidationState();
            return;
        }

        if (reportNeedsShippingAddress() && view !== 'submit-address') {
            openSubmitAddressView();
            return;
        }

        if (view === 'submit-address') {
            const api = global.OrderTrackerAddressUpdate;
            if (!api?.validateForm?.()) return;
            persistSubmitAddressSnapshot();
        }

        const url = buildFinaliseUrl();
        if (!url) {
            panelErrorMessage =
                tr.reportIssueFinaliseError ||
                'Something went wrong submitting your report. Please try again.';
            showDetailsValidationState();
            return;
        }

        const payload = buildSubmitSolutionsPayload();
        if (!payload.items.length || payload.items.some((item) => !item.lineId)) {
            panelErrorMessage =
                tr.reportIssueFinaliseIncomplete ||
                'Please complete this reported issue before finalising.';
            showDetailsValidationState();
            return;
        }
        if (reportNeedsShippingAddress() && !payload.shippingAddress?.shipTo) {
            panelErrorMessage =
                tr.reportIssueFinaliseError ||
                'Something went wrong submitting your report. Please try again.';
            showDetailsValidationState();
            return;
        }

        setFinaliseBusy(true);
        try {
            const submittedIssues = draft.issues.slice();
            const response = await postSubmitSolutions(url, payload);
            const ok = isSaveSolutionOk(response);
            const order = draft.order;
            removeDraftForOrder(order);
            draft = createEmptyDraft(order);
            draft.issues = [];
            pendingIssue = null;
            pendingFurthestStep = null;
            pendingIssueReopened = false;
            clearValidationErrors();
            renderSuccessPanel({ issues: submittedIssues, response, ok });
        } finally {
            setFinaliseBusy(false);
        }
    }

    /* --- Actions --- */

    function openDrawerForLine(lineKey) {
        pendingFurthestStep = null;
        pendingIssueReopened = false;
        pendingIssue = buildPendingFromLine(lineKey);
        editingIssueIndex = null;
        if (!pendingIssue) return;
        openDrawerStep('issue');
    }

    function handleAction(action, btn) {
        if (
            uploadInFlight &&
            ['drawer-close', 'drawer-next', 'drawer-back', 'drawer-finish'].includes(action)
        ) {
            return;
        }

        switch (action) {
            case 'close':
                exitReportIssueMode({ clearDraft: false });
                break;
            case 'discard':
                saveAndCloseReportIssue();
                break;
            case 'finalise':
                finaliseReport();
                break;
            case 'show-details':
                if (!(draft?.issues?.length > 0)) break;
                showDetailsView();
                break;
            case 'report-another':
                clearValidationErrors();
                showSelectorView();
                break;
            case 'open-drawer': {
                const lineKey = btn.dataset.oaReportLineKey;
                if (!lineKey) break;
                const entry = itemRegistry.get(lineKey);
                const lineQty = entry
                    ? Math.max(1, parseInt(entry.item.quantity, 10) || 1)
                    : parseInt(btn.closest('[data-oa-report-controls]')?.dataset.oaReportLineQty, 10) || 0;
                if (getRemainingQtyForLine(lineKey, lineQty) <= 0) break;
                clearValidationErrors();
                openDrawerForLine(lineKey);
                break;
            }
            case 'drawer-close':
                pendingIssue = null;
                pendingFurthestStep = null;
                pendingIssueReopened = false;
                editingIssueIndex = null;
                drawerStep = null;
                drawerContext = null;
                getOrderTrackerDrawer()?.close();
                break;
            case 'drawer-next':
                goNextStep();
                break;
            case 'drawer-back':
                goBackStep();
                break;
            case 'drawer-finish':
                clearValidationErrors();
                finishPendingIssue();
                break;
            case 'edit-step': {
                if (uploadInFlight) return;
                const step = btn.dataset.oaReportStep;
                syncPendingFromDom();
                if (stepsForPendingIssue().includes(step)) openDrawerStep(step);
                break;
            }
            case 'remove-media': {
                if (uploadInFlight) return;
                const mi = parseInt(btn.dataset.mediaIndex, 10);
                if (Number.isFinite(mi)) removeMediaAt(mi);
                break;
            }
            case 'remove-card-media': {
                const ii = parseInt(btn.dataset.oaReportIssueIndex, 10);
                const mi = parseInt(btn.dataset.mediaIndex, 10);
                if (!draft?.issues?.[ii]?.media || !Number.isFinite(mi)) return;
                if (view === 'contact') syncContactSupportCommentsFromDom();
                draft.issues[ii].media.splice(mi, 1);
                clearValidationErrors();
                if (view === 'contact' && contactSupportIssueIndex === ii) {
                    renderContactSupportPanel();
                } else {
                    showDetailsView();
                }
                break;
            }
            case 'remove-issue': {
                const tr = t();
                const ii = parseInt(btn.dataset.oaReportIssueIndex, 10);
                if (!draft?.issues || !Number.isFinite(ii)) return;
                const ok = global.confirm(
                    tr.reportIssueRemoveConfirm ||
                        'Remove this reported issue? This will update your saved progress.'
                );
                if (!ok) return;

                if (view === 'contact') syncContactSupportCommentsFromDom();
                draft.issues.splice(ii, 1);
                clearValidationErrors();
                if (contactSupportIssueIndex === ii) {
                    contactSupportIssueIndex = null;
                } else if (
                    contactSupportIssueIndex != null &&
                    contactSupportIssueIndex > ii
                ) {
                    contactSupportIssueIndex -= 1;
                }
                if (sparePartsIssueIndex === ii) {
                    sparePartsIssueIndex = null;
                    sparePartsState = createEmptySparePartsState();
                } else if (sparePartsIssueIndex != null && sparePartsIssueIndex > ii) {
                    sparePartsIssueIndex -= 1;
                }
                if (draft.issues.length) {
                    flushPersist();
                } else if (draft.order) {
                    removeDraftForOrder(draft.order);
                }
                syncSelectorLineUi();
                if (!draft.issues.length) {
                    showSelectorView();
                } else {
                    showDetailsView();
                }
                break;
            }
            case 'edit-issue': {
                const ii = parseInt(btn.dataset.oaReportIssueIndex, 10);
                if (!draft?.issues?.[ii]) return;
                if (view === 'contact') syncContactSupportCommentsFromDom();
                clearValidationErrors();
                pendingIssue = cloneIssueForEdit(draft.issues[ii], ii);
                pendingFurthestStep = null;
                pendingIssueReopened = false;
                openDrawerStep(savedIssueIsComplete(pendingIssue) ? 'review' : 'issue');
                break;
            }
            case 'choose-solution': {
                const ii = parseInt(btn.dataset.oaReportIssueIndex, 10);
                if (Number.isFinite(ii)) {
                    if (view === 'contact') syncContactSupportCommentsFromDom();
                    clearValidationErrors();
                    openSolutionDrawer(ii);
                }
                break;
            }
            case 'solution-confirm':
                clearValidationErrors();
                confirmSolutionSelection();
                break;
            case 'solution-back':
                closeSolutionDrawer();
                break;
            case 'contact-back':
                exitContactSupportView();
                break;
            case 'contact-select-solution':
                selectContactSupportSolution();
                break;
            case 'resend-back':
                exitResendView();
                break;
            case 'resend-continue':
                continueResendSelection();
                break;
            case 'resend-add': {
                const code = btn.dataset.oaReportResendCode;
                const alreadySelected = Object.keys(resendState.quantities || {}).some(
                    (key) => (parseInt(resendState.quantities[key], 10) || 0) > 0
                );
                if (!code || alreadySelected) break;
                setResendQuantity(code, 1);
                break;
            }
            case 'resend-undo': {
                const code = btn.dataset.oaReportResendCode;
                if (!code) break;
                setResendQuantity(code, 0);
                break;
            }
            case 'resend-qty-inc': {
                const code = btn.dataset.oaReportResendCode;
                if (!code) break;
                setResendQuantity(code, (resendState.quantities[code] || 0) + 1);
                break;
            }
            case 'resend-qty-dec': {
                const code = btn.dataset.oaReportResendCode;
                const current = parseInt(resendState.quantities[code], 10) || 0;
                if (!code || current <= 1) break;
                setResendQuantity(code, current - 1);
                break;
            }
            case 'spares-back':
                exitSparePartsView();
                break;
            case 'spares-continue':
                if (selectedSpareCount() === 0) break;
                if (drawerContext === 'fixings') closeFixingsDrawer({ commit: false });
                persistSpareQtyToIssue();
                showSparePartsSummaryView();
                break;
            case 'submit-address-back':
                backFromSubmitAddressView();
                break;
            case 'spares-summary-back':
                backFromSparePartsSummaryView();
                break;
            case 'spares-summary-select':
                confirmSparePartsSummary();
                break;
            case 'spares-summary-edit-parts':
                editSparePartsFromSummary();
                break;
            case 'spares-tab':
                setSparePartsTab(btn.dataset.oaReportSparesTab);
                break;
            case 'spares-add': {
                const key = btn.dataset.oaReportPartKey;
                const scope = btn.dataset.oaReportSparesQtyScope;
                setSpareQty(scope, key, 1);
                break;
            }
            case 'spares-qty-inc': {
                const key = btn.dataset.oaReportPartKey;
                const scope = btn.dataset.oaReportSparesQtyScope;
                const map = qtyMapForScope(scope);
                setSpareQty(scope, key, (map[key] || 0) + 1);
                break;
            }
            case 'spares-qty-dec': {
                const key = btn.dataset.oaReportPartKey;
                const scope = btn.dataset.oaReportSparesQtyScope;
                const map = qtyMapForScope(scope);
                setSpareQty(scope, key, (map[key] || 0) - 1);
                break;
            }
            case 'fixings-open-drawer':
                openFixingsDrawer();
                break;
            case 'fixings-drawer-back':
                closeFixingsDrawer({ commit: false });
                break;
            case 'fixings-drawer-add':
                closeFixingsDrawer({ commit: true });
                break;
            case 'fixings-search-clear': {
                sparePartsState.fixingsSearch = '';
                const input = getOrderTrackerDrawer()?.querySelector('[data-oa-report-fixings-search]');
                if (input) input.value = '';
                const clearBtn = getOrderTrackerDrawer()?.querySelector(
                    '[data-oa-report-action="fixings-search-clear"]'
                );
                if (clearBtn) clearBtn.hidden = true;
                refreshFixingsDrawerLive();
                break;
            }
            case 'qty-inc':
                stepReportedQty(1);
                break;
            case 'qty-dec':
                stepReportedQty(-1);
                break;
            case 'success-done':
                exitReportIssueMode({ clearDraft: true, skipUnsavedConfirm: true });
                if (typeof global.__orderTrackerRefetchOrder === 'function') {
                    global.__orderTrackerRefetchOrder();
                }
                break;
            default:
                break;
        }
    }

    function handleDelegatedClick(ev) {
        const fileLabel = ev.target.closest('.order-report-issue__upload-area');
        if (fileLabel && getOrderTrackerDrawer()?.contains(fileLabel)) {
            const input = fileLabel.querySelector('[data-oa-report-file]');
            if (input && ev.target !== input) {
                // label handles click
            }
        }

        const actionBtn = ev.target.closest('[data-oa-report-action]');
        if (!actionBtn) return;

        const drawer = getOrderTrackerDrawer();
        const inDrawer = drawer?.contains(actionBtn);
        const inRoot = getRoot()?.contains(actionBtn);
        const inStep = getStepEl()?.contains(actionBtn);

        if (inDrawer) {
            if (!drawer.classList.contains('order-tracker-drawer--open')) return;
            ev.preventDefault();
            handleAction(actionBtn.dataset.oaReportAction, actionBtn);
            return;
        }

        if (!editModeActive && actionBtn.dataset.oaReportAction !== 'open-drawer') return;
        if (!inRoot && !inStep) return;

        ev.preventDefault();
        handleAction(actionBtn.dataset.oaReportAction, actionBtn);
    }

    function handleChange(ev) {
        const target = ev.target;
        if (!target) return;

        if (target.name === 'report-issue-type' && target.type === 'radio') {
            if (pendingIssue) pendingIssue.issueType = target.value;
            showDrawerError('');
            return;
        }

        if (target.name === 'report-preferred-solution' && target.type === 'radio') {
            if (pendingIssue) pendingIssue.preferredSolution = target.value;
            showDrawerError('');
            syncPreferredReplacementNotice();
            return;
        }

        if (target.name === 'report-solution-type' && target.type === 'radio') {
            pendingSolutionType = target.value;
            showDrawerError('');
            syncReplacementBlockedNotice();
            return;
        }

        if (target.name === 'report-resend-disposition' && target.type === 'radio') {
            resendState.disposition = target.value;
            resendState.fieldError = '';
            renderResendPanel();
            return;
        }

        if (target.matches?.('[data-oa-report-file]')) {
            handleFilesSelected(target.files);
            target.value = '';
        }

        if (target.matches?.('[data-oa-report-spares-qty-input]')) {
            const scope = target.dataset.oaReportSparesQtyScope;
            const key = target.dataset.oaReportPartKey;
            const max = sparePartMaxQty(findSparePart(scope, key));
            let n = parseInt(target.value, 10);
            if (!Number.isFinite(n) || n < 1) n = 1;
            if (n > max) n = max;
            setSpareQty(scope, key, n);
        }
    }

    function handleInput(ev) {
        const target = ev.target;
        if (!target?.matches?.('[data-oa-report-fixings-search]')) return;
        sparePartsState.fixingsSearch = target.value || '';
        const clearBtn = target
            .closest('.order-report-issue__fixings-search')
            ?.querySelector('[data-oa-report-action="fixings-search-clear"]');
        if (clearBtn) clearBtn.hidden = !String(target.value || '').length;
        refreshFixingsDrawerLive();
    }

    function initOrderReportIssue() {
        if (handlersBound) return;
        handlersBound = true;

        document.getElementById('order-report-issue-start')?.addEventListener('click', () => {
            beginReportIssueFlow();
        });

        document.addEventListener('click', handleDelegatedClick);
        document.addEventListener('change', handleChange);
        document.addEventListener('input', handleInput);
        global.addEventListener('beforeunload', handleBeforeUnload);

        getOrderTrackerDrawer()?.addEventListener('order-tracker-drawer:closed', () => {
            if (uploadInFlight) return;
            if (drawerContext === 'fixings') {
                sparePartsState.drawerFixingQty = {};
                sparePartsState.fixingsSearch = '';
                drawerContext = null;
                getOrderTrackerDrawer()?.classList.remove('order-tracker-drawer--fixings');
                renderSparePartsPanel();
                updateHeaderActions();
                return;
            }
            if (drawerContext === 'solution') {
                solutionIssueIndex = null;
                pendingSolutionType = null;
                drawerContext = null;
                return;
            }
            if (drawerStep && pendingIssue && editingIssueIndex == null && !pendingIssue.issueType) {
                pendingIssue = null;
                pendingFurthestStep = null;
                pendingIssueReopened = false;
                drawerStep = null;
                drawerContext = null;
            } else if (drawerStep) {
                drawerStep = null;
                drawerContext = null;
            }
        });
    }

    global.OrderReportIssueFlow = {
        initOrderReportIssue,
        rebuildReportIssueRegistry,
        updateReportIssueCtaVisibility,
        syncSelectorLineUi,
        isShipmentEligibleForReportIssue,
        isLineEligibleForReportIssue,
        makeLineKey,
        isReportIssueFlowActive: () => editModeActive,
        hasUnsavedChanges,
        exitReportIssueMode,
    };
})(window);
